import { createSlice, current } from "@reduxjs/toolkit";
import { initialState, procedure as newProcedureRowItem, procedure } from "./initialState";
import { RESPONSIBILITY_TYPES, ROLE_MANAGER } from "../../../../utilities/dictionaryConstants";
import i18n from "../../../../utilities/i18n";
import { decimalConverter, YMD_TO_MDY } from "../../../../utilities/commonUtilities";
import { format, addDays } from 'date-fns';


import {
    generateAdditionalclaimSaveData, validateDiagnosis,
    generateBillingInfoUpdateData, generateBillingTabSaveData,
    generateClaimCommonUpdateData, generateServiceLineSaveData,
    generateServiceLineUpdateData,
    getServiceDateFrom, getServiceDateTo, validateModifier,
    generateAdditionaleUpdateData,
    setWarningFromServer,
    checkForGapsBetweenIcd,
    validateIsValidDxPointer,
    updateDXpointer,
    findPId,
    FieldPermissionGenerator,
    claimStagesForEscalateDropdown,
    reportsToDisplay,
} from "./claimDataGenerator";

import {
    ActivateInactiveClaim,
    addNewProcedureFromChargePanel,
    fetchIdentifiers,
    fetchUnAppliedFee,
    getChargePanelsList,
    GetCPTCodeListWithDate,
    getEditDetails,
    getPatientDetails,
    getPatientHistory,
    getSearchClaimsTableData,
    GetServiceLocationPOSList,
    getClaimWorkflow,
    getClaimActivityLogs,
    completeClaimAPI,
    updateClaimAPI,
    fetchPracticeUsers,
    fetchSupervisorEscalationDetails,
    deEscalateClaimAPI,
    fetchClaimPayments,
    fetchPaymentDetail,
    fetchSupportReasons,
    submitSupportRequest,
    deleteProcedureItem,
    ClaimStatusChange,
    arFollowupNotes,
    ARClaimNotes,
    ListArClaimNotes,
    onEditArClaimNotes,
    onDeleteArClaimNotes,
    getArClaimFilterDataSet,
    getFieldList,
    fetchWorkflowDetails,
    fetchAuditorIssues,
    submitAuditorIssue,
    DeleteAuditorIssue,
    reproveStage,
    approveStage,
    getEscalationComments,
    getAuditorComments,
    submitEscalationResponse,
    escalateToManager,
    saveClaimOnHold,
    releaseClaimHold,
    fetchClaimHoldStatuses
} from "./asyncThunkAPI";
import { TAB1 } from "../../../../utilities/staticConfigs";


const claimBetaSlice = createSlice({
    name: 'claimBeta',
    initialState,
    reducers: {
        openModal: (state) => { state.isOpen = true; },
        closeModal: (state) => { state.isOpen = false; },
        clearData: (state) => { state.ClaimSummayData = null; },
        resetToInitial: () => initialState,
        /**
         * setting opened current tab here
         * @param {*} state 
         * @param {*} action 
         */
        setOpenTab: (state, action) => {
            state.tabsOpen = action.payload;
        },

        /**
         * @description Handles Last 10 tabs sorting logic
         * @param {*} state 
         * @param {string} action.payload receives the name of the field to be sorted 
         */
        setLast10Sort: (state, action) => {
            const sortField = action.payload;

            // Set sort field and order type
            state.last10Table.sortField = sortField;
            state.last10Table.orderType = state.last10Table.orderType === "" ? "-" : "";

            // Reset all ordering flags to false
            const resetOrderingFlags = () => {
                state.last10Table.initialOrderingPatientName = true;
                state.last10Table.initialOrderingDos = true;
                state.last10Table.initialOrderingBillDate = true;
                state.last10Table.initialOrderingClaimId = true;
            };

            // Set the appropriate ordering flag based on sort field
            const setOrderingFlag = (flagName) => {
                resetOrderingFlags();
                state.last10Table[flagName] = false;
            };

            switch (sortField) {
                case "patient__full_name":
                    setOrderingFlag('initialOrderingPatientName');
                    break;
                case "custom_claim_id":
                    setOrderingFlag('initialOrderingClaimId');
                    break;
                case "service_from_date":
                    setOrderingFlag('initialOrderingDos');
                    break;
                case "last_billed_date":
                    setOrderingFlag('initialOrderingBillDate');
                    break;
                default:
                    resetOrderingFlags();
                    break;
            }
        },

        /**
         * @description Handles the value change in the SearchClaims Tab Filter inputs
         * @param {*} state 
         * @param {*} action 
         */
        setSearchClaimFilterData: (state, action) => {
            const field = action.payload.field;
            const value = action.payload.value;

            state.searchClaimsTable[field] = value
        },

        /**
         * @description Handles Search Claims Table sorting logic
         * @param {*} state 
         * @param {string} action.payload receives the name of the field to be sorted 
         */
        setSearchClaimsSort: (state, action) => {
            const sortField = action.payload;

            // Set sort field and order type
            state.searchClaimsTable.sortField = sortField;
            state.searchClaimsTable.orderType = state.searchClaimsTable.orderType === "" ? "-" : "";

            // Reset all ordering flags to false
            const resetOrderingFlags = () => {
                state.searchClaimsTable.initialOrderingPatientName = true;
                state.searchClaimsTable.initialOrderingDos = true;
                state.searchClaimsTable.initialOrderingBillDate = true;
                state.searchClaimsTable.initialOrderingClaimId = true;
            };

            // Set the appropriate ordering flag based on sort field
            const setOrderingFlag = (flagName) => {
                resetOrderingFlags();
                state.searchClaimsTable[flagName] = false;
            };

            switch (sortField) {
                case "patient__full_name":
                    setOrderingFlag('initialOrderingPatientName');
                    break;
                case "custom_claim_id":
                    setOrderingFlag('initialOrderingClaimId');
                    break;
                case "service_from_date":
                    setOrderingFlag('initialOrderingDos');
                    break;
                case "last_billed_date":
                    setOrderingFlag('initialOrderingBillDate');
                    break;
                default:
                    resetOrderingFlags();
                    break;
            }
        },

        resetSearchClaimData: (state) => {
            state.searchClaimsTable = {
                ...initialState.searchClaimsTable,
                isReset: true
            }
            state.apiError = "";
            state.dataLoading = false
        },
        clearSearchClaimResetFlag: (state) => {
            state.searchClaimsTable.isReset = false;
        },

        /**
         * reducer function to save patient initial data to state on patient search select
         * function will dispatch when a particular patient search with @name @id @dob and click on the search result
         * this function only used to create a new claim
         * @param {*} state 
         * @param {*} actions 
         */
        onPatientDropDownSelectAction: (state, actions) => {
            let { commonData, patientSelected, patientAdvSearchData, selectPatient } = actions.payload;
            let data = {
                ...commonData,
                claim_pk: 'add_new'
            }

            state.professionalClaim.push(data);
            state.patientSelectedData = patientSelected;
            state.patientAdvanceSearchData = patientAdvSearchData;
            state.selectedPatient = selectPatient;
            state.claimPK = ""
            state.diagnosis_pk = "";
            state.billing_info_pk = '',
                state.addition_info_pk = ""
            state.professionalClaimDetails = data;
            state.infoLoaded = true;
            /**pushing all pks */
            const pks = {
                ...commonData.pks,
                claim_pk: 'add_new',

            }
            state.pks.push(pks);
        },

        /**
         * function is used to update value of parent component
         * action contains field to be updated and value and claimPK
         * @param {*} state 
         * @param {*} actions 
         */
        updateParentComponentData: (state, actions) => {
            let { field, value, claimStatus,claim_sub_status } = actions.payload;
            let { professionalClaimDetails, serviceLineDetails, claimSubStatus } = current(state);
            let updateValue = {
                ...professionalClaimDetails,
                [field]: value ?? null
            }

            //  Whenever there's a change in the authorization_referral change its corresponding identifier into empty string
            if (field == "authorization_referral") {
                updateValue = {
                    ...updateValue,
                    identifier: null,
                    identifierData:[]
                }
            }

            /**checking field is inactive and if value is true making active to false because in_active is true */
            if (field == "claim_inactive" && value == true) {
                updateValue = {
                    ...updateValue,
                    active: false
                }
            }
            /**checking field is inactive and if value is false making active to true because in_active is false
             * also making activeModal to true to show active modal window
             */
            if (field == "claim_inactive" && value == false) {
                updateValue = {
                    ...updateValue,
                    active: true
                }

                state.activeClaimModal = true;
            }

            if (field === "claim_status_id") {
                updateValue = {
                    ...updateValue,
                    "claim_sub_status_id": null
                }
            }

            if (field === "claim_sub_status_id") {
                updateValue = {

                    ...updateValue,
                    "claim_sub_status_id": value ?? null
                }
            }
            /**
             * updating profession claim details immediately
             */
            state.professionalClaimDetails = updateValue;

            /**
             * when updating claim_status in parent component it is will reflect in procedure 
             * code below will do that
             */
            let claimStatusError = [];
            if (field === "claim_status_id") {
                let claimStatusPriority = claimStatus?.filter((element) => element.id === value);

                if (claimStatusPriority.length > 0) {
                    serviceLineDetails?.procedures?.forEach((procedure, index) => {
                        let checked = claimStatusPriority[0]?.priorities?.includes(procedure.responsibility_type);
                        if (checked) {
                            let updatedProcedure = {
                                ...procedure,
                                claim_status: value,
                                claim_sub_status: null,
                            };

                            // Update the procedure at the current index
                            state.serviceLineDetails.procedures[index] = updatedProcedure;

                            if (claimSubStatus.length > 0) {
                                let filterData = claimSubStatus?.filter(item => item.parent_claim_status_ids?.includes(value));
                                state.serviceLineDetails.claim_sub_status[index] = filterData;
                            }
                        }
                        else {
                            let responsibility = RESPONSIBILITY_TYPES.filter((item) => item.id == procedure.responsibility_type);
                            let errordata = `Procedure ${index + 1} with responsibility ${responsibility[0].name}`
                            claimStatusError.push(errordata)
                            let updatedProcedure = {
                                ...procedure,
                                claim_status: null,
                                claim_sub_status: null,
                            };
                            // Update the procedure at the current index
                            state.serviceLineDetails.procedures[index] = updatedProcedure;
                            state.serviceLineDetails.claim_sub_status[index] = [];
                        }
                    });
                }
                else {
                    serviceLineDetails?.procedures?.forEach((procedure, index) => {
                        let updatedProcedure = {
                            ...procedure,
                            claim_status: null,
                            claim_sub_status: null,
                        };
                        // Update the procedure at the current index
                        state.serviceLineDetails.procedures[index] = updatedProcedure;
                        state.serviceLineDetails.claim_sub_status[index] = [];
                    });
                }
                let message = "";
                if (claimStatusError.length > 0) {
                    message += claimStatusError.join(' and ') + ". The selected status is not under these responsibilities.";
                    state.notifyMessage = message;
                    state.showNotification = true;
                    state.statusTag = "error";
                }
            }
            if (field == "claim_sub_status_id" && value) {
                serviceLineDetails?.procedures?.forEach((procedure, index) => {
                    if (claim_sub_status.length > 0) {
                        claim_sub_status.forEach((status_data) => {
                            if (status_data.id === value) {
                                let updatedProcedure = {
                                    ...procedure,
                                    claim_sub_status: value ?? null,
                                };
                                state.serviceLineDetails.procedures[index] = updatedProcedure;
                            }
                        })
                    }
                })
            }
        },
        /**
         * function is used to update value of parent component
         * action contains field to be updated and value and claimPK
         * @param {*} state 
         * @param {*} actions 
         */
        updateBillingInfoData: (state, actions) => {
            let { field, value } = actions.payload;
            let { billingInfoDetails, 
                // professionalClaimDetails, professionalClaim 
            } = current(state);
            let updateValue;
            if (field === "service_locations" || field === "facility_location") {
                let service_location_id = value.split("||")[0];
                let service_location_type = value.split("||")[1];
                updateValue = {
                    ...billingInfoDetails,
                    [field]: parseInt(service_location_id),
                    service_location_value: value ??  null,
                    service_location: { id: service_location_id ?? null, location_type: service_location_type ??  null  },
                }
            }
            else {
                updateValue = {
                    ...billingInfoDetails,
                    [field]: value
                }
                if (value) {
                    state.fee_field_status = {
                        all:true,
                        single:true}
                } else {
                    state.fee_field_status = {
                        all:true,
                        single:true}
                }
            }
            /**
             * updating billing info details immediately
             */
            state.billingInfoDetails = updateValue;

            /**
             * resetting authorization data on billing provider change
             */
            //commenting since we don't need this currently may use later.
            // if (field == "billing_provider") {
            //     let updateValue = {
            //         ...professionalClaimDetails,
            //         "identifier": "",
            //         "authorization_referral": ""
            //     }

            //     state.professionalClaimDetails = updateValue;
            //     /**
            //           * changing value of professional claim array value
            //     */

            //     let index1 = professionalClaim.findIndex((item) => item.claim_pk === claim_pk);
            //     if (index1 >= 0) {
            //         state.professionalClaim[index1] = updateValue;
            //     }
            // }

        },

        /**
         * function used to update payer details
         * @param {*} state 
         * @param {*} actions 
         */
        updatePayerInfoDetails: (state, actions) => {
            let { fieldType, priority, value } = actions.payload;
            let {billingInfoDetails,
            } = current(state);
            let priorityInDigit = 0;
            if (priority === 'primary')
                priorityInDigit = 1;
            else if (priority === 'secondary')
                priorityInDigit = 2;
            else if (priority === 'tertiary')
                priorityInDigit = 3;
            let arryObj = {};
            let updateValue = {
                ...billingInfoDetails
            }
            let payer_insurance=[...(Array.isArray(billingInfoDetails.payer_info) ? billingInfoDetails.payer_info : [])];
            let arrInsure = [...(Array.isArray(billingInfoDetails.payerInsuranceList) ? billingInfoDetails.payerInsuranceList : [])];
            /**
             * getting data using priority
             */

            let payerObj = billingInfoDetails?.payerInsuranceList?.filter((pIObj) => pIObj.priority === priorityInDigit);
            if (payerObj && payerObj.length > 0) {
                arryObj = { ...payerObj[0] };
                delete arryObj.id;
            }
            /**
             * storing primary insurance data here
             */
            if (priority === 'primary') {
                arryObj.priority = 1;
                if (fieldType === 'insurance') {
                    if (value && value != '0') {
                        state.fee_field_status = {
                        all:true,
                        single:true}
                        let primaryInsureObj = billingInfoDetails?.primaryInsuranceDataList?.filter((pIObj) => pIObj.pId === value);
                        if (primaryInsureObj) {
                            updateValue = {
                                ...updateValue,
                                insurancePrimary: value,
                                primarypolicyId: primaryInsureObj[0].policy_id,
                                primaryPayerId: primaryInsureObj[0].ins_payer_id
                            }
                            arryObj.policy_id = primaryInsureObj[0].policy_id;
                            arryObj.practice_insurance_id = primaryInsureObj[0].id;
                            arryObj.pId=primaryInsureObj[0].pId
                            arryObj.Id=primaryInsureObj[0].id
                        }
                        else {
                            updateValue = {
                                ...updateValue,
                                insurancePrimary: "",
                                primarypolicyId: "",
                                primaryPayerId:""
                            }
                            delete arryObj['priority'];
                            delete arryObj['practice_insurance_id'];
                            delete arryObj['policy_id'];
                            delete arryObj['pId'];
                            delete arryObj['Id'];
                        }
                    }
                    else {
                        updateValue = {
                            ...updateValue,
                            insurancePrimary: "",
                            primarypolicyId: "",
                            primaryPayerId:""
                        }
                        state.fee_field_status = {
                        all:true,
                        single:true}
                        delete arryObj['priority'];
                        delete arryObj['practice_insurance_id'];
                        delete arryObj['policy_id'];
                        delete arryObj['pId'];
                        delete arryObj['Id'];
                    }
                }
                if (fieldType === 'policyId') {
                    updateValue = {
                        ...updateValue,
                        primarypolicyId: value,
                    }
                    arryObj.policy_id = value;
                }
            }
            /**
            * storing secondary insurance data here
            */
            if (priority === 'secondary') {
                arryObj.priority = 2;
                if (fieldType === 'insurance') {
                    if (value && value != '0') {
                        let secondaryInsureObj = billingInfoDetails?.secondaryInsuranceDataList?.filter((pIObj) => pIObj.pId === value);
                        if (secondaryInsureObj) {
                            updateValue = {
                                ...updateValue,
                                insurancesecondary: value,
                                secondarypolicyId: secondaryInsureObj[0].policy_id,
                                secondaryPayerId: secondaryInsureObj[0].ins_payer_id
                            }
                            arryObj.policy_id = secondaryInsureObj[0].policy_id;
                            arryObj.practice_insurance_id = secondaryInsureObj[0].id;
                            arryObj.pId=secondaryInsureObj[0].pId
                            arryObj.Id=secondaryInsureObj[0].id
                        }
                        else {
                            updateValue = {
                                ...updateValue,
                                insurancesecondary: "",
                                secondarypolicyId: "",
                                secondaryPayerId:""
                            }
                            delete arryObj['priority'];
                            delete arryObj['practice_insurance_id'];
                            delete arryObj['policy_id'];
                            delete arryObj['pId'];
                            delete arryObj['Id'];
                        }
                    }
                    else {
                        updateValue = {
                            ...updateValue,
                            insurancesecondary: "",
                            secondarypolicyId: "",
                            secondaryPayerId:""
                        }
                        delete arryObj['priority'];
                        delete arryObj['practice_insurance_id'];
                        delete arryObj['policy_id'];
                        delete arryObj['pId'];
                        delete arryObj['Id'];
                    }
                }
                if (fieldType === 'policyId') {
                    updateValue = {
                        ...updateValue,
                        secondarypolicyId: value,
                    }
                    arryObj.policy_id = value;
                }
            }
            /**
            * storing tertiary insurance data here
           */
            if (priority === 'tertiary') {
                arryObj.priority = 3;
                if (fieldType === 'insurance') {
                    if (value && value != '0') {
                        let tertiaryInsureObj = billingInfoDetails?.tertiaryInsuranceDataList.filter((pIObj) => pIObj.pId === value);
                        if (tertiaryInsureObj) {
                            updateValue = {
                                ...updateValue,
                                insuranceTertiary: value,
                                tertiarypolicyId: tertiaryInsureObj[0].policy_id,
                                tertiaryPayerId: tertiaryInsureObj[0].ins_payer_id
                            }
                            arryObj.policy_id = tertiaryInsureObj[0].policy_id;
                            arryObj.practice_insurance_id = tertiaryInsureObj[0].id;
                            arryObj.pId=tertiaryInsureObj[0].pId
                            arryObj.Id=tertiaryInsureObj[0].id
                        } else {
                            updateValue = {
                                ...updateValue,
                                insuranceTertiary: "",
                                tertiarypolicyId: '',
                                tertiaryPayerId: ''
                            }
                            delete arryObj['priority'];
                            delete arryObj['practice_insurance_id'];
                            delete arryObj['policy_id'];
                            delete arryObj['pId'];
                            delete arryObj['Id'];
                        }
                    } else {
                        updateValue = {
                            ...updateValue,
                            insuranceTertiary: "",
                            tertiarypolicyId: '',
                            tertiaryPayerId:''
                        }
                        delete arryObj['priority'];
                        delete arryObj['practice_insurance_id'];
                        delete arryObj['policy_id'];
                        delete arryObj['pId'];
                        delete arryObj['Id'];
                    }
                }
                if (fieldType === 'policyId') {
                    updateValue = {
                        ...updateValue,
                        tertiarypolicyId: value,
                    }
                    arryObj.policy_id = value;
                }
            }

            /**
             * making new payer info details as per the changes
             */
            if (Object.keys(arryObj).length == 0) {
                const arr_index = arrInsure.findIndex((item) => item.priority == priorityInDigit);
                if (arr_index >= 0) {
                    arrInsure.splice(arr_index, 1);
                }

                const payer_index= payer_insurance.findIndex((item) => item.priority == priorityInDigit);
                if (payer_index >= 0) {
                    payer_insurance.splice(payer_index, 1);
                }

            }
            else {
                const payer_index = payer_insurance.findIndex((item) => item.priority == priorityInDigit);
                if (payer_index >= 0) {
                    payer_insurance[payer_index] = arryObj;
                } else {
                    payer_insurance.push(arryObj)
                }
                const index = arrInsure.findIndex((item) => item.priority == priorityInDigit);
                if (index >= 0) {
                    arrInsure[index] = arryObj;
                }
                else {
                    arrInsure.push(arryObj);
                }
            }

            // Filter to remove incomplete entries
            arrInsure = arrInsure.filter(
                (item) => item.practice_insurance_id && item.policy_id
            );

            if (Object.keys(arrInsure).length > 0) {
                updateValue = {
                    ...updateValue,
                    ["payerInsuranceList"]: arrInsure,
                    ['payer_info']: payer_insurance,
                }

                /**
            * updating billing info details immediately
            */
                state.billingInfoDetails = updateValue;

            }
            else {
                updateValue = {
                    ...updateValue,
                    ["payerInsuranceList"]: arrInsure,
                    ['payer_info']: []
                }

                /**
            * updating billing info details immediately
            */
                state.billingInfoDetails = updateValue;
            }
        },
        /**
         * function is used to update value of parent component
         * action contains field to be updated and value and claimPK
         * @param {*} state 
         * @param {*} actions 
         */
        updateAdditionalInfoData: (state, actions) => {
            let { field, value } = actions.payload;
            let { additionalClaimDetails } = current(state);
            let updateValue = {
                ...additionalClaimDetails,
                [field]: value ??  null
            }

            if (field == "accident") {
                updateValue = {
                    ...updateValue,
                    "accident_date": null,
                    "state": null,
                    "other_accident": null
                }
            }
            /**
             * updating billing info details immediately
             */
            state.additionalClaimDetails = updateValue;
            const fieldsToUpdate = ["clinic_site_name", "lab_sales_rep"];

            if (fieldsToUpdate.includes(field)) {
                state.fee_field_status = {
                    all: true,
                    single: true
                };
            }
        },

        /**
         * function to update cpt of procedure
         * @param {*} state 
         * @param {*} actions 
         */
        updateProcedureCPT: (state, actions) => {
            let { serviceLineDetails, serviceLocationPosList } = current(state);
            let { valueList, field, rowId } = actions.payload;
            if (valueList.length > 0) {
                let newData = { ...serviceLineDetails?.procedures[rowId] };
                state.fee_field_status = {
                        all:true,
                        single:true}
                newData = {
                    ...newData,
                    [field]: valueList[0].id ?? null,
                    [field + 'List']: valueList,
                    ['proced_description']: valueList[0].description ??  null
                }
                /**
                 * checking and updating procedure value list have pos with value
                 */
                if (valueList[0].pos) {
                    let posList = [{
                        id: valueList[0]?.pos?.id ?? null,
                        label: valueList[0]?.pos?.label ?? null,
                        name: valueList[0]?.pos?.name ?? null,
                    }]
                    newData = { ...newData, pos_id: valueList[0].pos.id, pos_id_List: posList };
                }
                else {
                    if (serviceLocationPosList.length > 0) {
                        let pos_List = [{
                            id: serviceLocationPosList[0]?.id ?? null,
                            label: serviceLocationPosList[0]?.label ?? null,
                            name: serviceLocationPosList[0]?.name ?? null,
                        }]

                        newData = { ...newData, pos_id: serviceLocationPosList[0].id, pos_id_List: pos_List };
                    }
                }

                /**
                 * checking for units
                 */
                if (valueList[0].units) {
                    newData = { ...newData, fee_units: valueList[0].units ?? 0 }
                }

                /**
                 * checking for tos
                 */
                if (valueList[0].tos) {
                    let tosList = [{
                        id: valueList[0]?.tos?.id ?? null,
                        label: valueList[0]?.tos?.name ?? null,
                        name: valueList[0]?.tos?.name ?? null,
                    }]
                    newData = { ...newData, tos_id: valueList[0].tos.id, tos_id_List: tosList };
                }
                if (valueList[0].fee) {
                    newData = { ...newData, fee: decimalConverter(valueList[0].fee ?? 0) }
                }

                if (valueList[0].units && valueList[0].fee) {
                    newData = { ...newData, charges: decimalConverter(valueList[0].fee) * parseInt(valueList[0].units) }
                }
                /**
                 * checking for modifier list 
                 */
                if (valueList[0].m1_id && valueList[0].m1_value.length > 0) {
                    newData = { ...newData, "m1_id": valueList[0].m1_id, "m1List": valueList[0].m1_value };
                }
                else {
                    newData = { ...newData, "m1_id": null, "m1List": [] };
                }
                if (valueList[0].m2_id && valueList[0].m2_value.length > 0) {
                    newData = { ...newData, "m2_id": valueList[0].m2_id, "m2List": valueList[0].m2_value };
                }
                else {
                    newData = { ...newData, "m2_id": null, "m2List": [] };
                }
                if (valueList[0].m3_id && valueList[0].m3_value.length > 0) {
                    newData = { ...newData, "m3_id": valueList[0].m3_id, "m3List": valueList[0].m3_value };
                }
                else {
                    newData = { ...newData, "m3_id": null, "m3List": [] };
                }
                if (valueList[0].m4_id && valueList[0].m4_value.length > 0) {
                    newData = { ...newData, "m4_id": valueList[0].m4_id , "m4List": valueList[0].m4_value  };
                }
                else {
                    newData = { ...newData, "m4_id": null, "m4List": [] };
                }


                state.serviceLineDetails.procedures[rowId] = newData;
            } else {
                state.fee_field_status = {
                        all:false,
                        single:false}
                let newData = { ...serviceLineDetails?.procedures[rowId] };
                newData = {
                    ...newData, [field]: null,
                    "pos_id": null,
                    "tos_id": null, "charges": '0',
                    "fee": '0',
                    'proced_description': null,
                    "pos_id_List": [],
                    "tos_id_List": [],
                    [field + 'List']: valueList
                }
                state.serviceLineDetails.procedures[rowId] = newData;
            }

            /**
           * modifier validation done here
           */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
        },

        /**
         * for update pos list in procedure
         * @param {*} state 
         * @param {*} actions 
         */
        updatePOSList: (state, actions) => {
            let { serviceLineDetails } = current(state);
            let { valueList, rowId } = actions.payload;
            let newData = { ...serviceLineDetails?.procedures[rowId] };
            if (valueList.length > 0) {
                let posList = [{
                    id: valueList[0]?.id,
                    label: valueList[0]?.drop_down_name,
                    name: valueList[0]?.drop_down_name,
                }]
                newData = { ...newData, 'pos_id': valueList[0].id ?? null };
                newData = { ...newData, 'pos_id_List': posList };
            }
            else {
                newData = { ...newData, 'pos_id': null };
                newData = { ...newData, 'pos_id_List': [] };
            }

            state.serviceLineDetails.procedures[rowId] = newData;
        },

        /**
         * for update tos list in procedure
         * @param {*} state 
         * @param {*} actions 
         */
        updateTOSList: (state, actions) => {
            let { serviceLineDetails } = current(state);
            let { valueList, rowId } = actions.payload;
            let newData = { ...serviceLineDetails?.procedures[rowId] };
            if (valueList.length > 0) {
                let posList = [{
                    id: valueList[0]?.id,
                    label: valueList[0]?.drop_down_name,
                    name: valueList[0]?.drop_down_name,
                }]
                newData = { ...newData, 'tos_id': valueList[0].id ?? null };
                newData = { ...newData, 'tos_id_List': posList };
            }
            else {
                newData = { ...newData, 'tos_id': null };
                newData = { ...newData, 'tos_id_List': [] };
            }

            state.serviceLineDetails.procedures[rowId] = newData;
        },

        /**
         * for update modifier list in procedure
         * @param {*} state 
         * @param {*} actions 
         */
        updateModifierList: (state, actions) => {
            let { serviceLineDetails } = current(state);
            let { valueList, name, rowId } = actions.payload;
            let newData = { ...serviceLineDetails?.procedures[rowId] };
            if (valueList.length > 0) {
                newData = { ...newData, [name]: valueList[0].id ?? null };
                newData = { ...newData, [name.split('_')[0] + 'List']: valueList };
                if(name=="m1_id"){
                    state.fee_field_status = {
                        all:true,
                        single:true}
                }
            }
            else {
                newData = { ...newData, [name]: null };
                newData = { ...newData, [name.split('_')[0] + 'List']: [] };
                state.fee_field_status = {
                        all:true,
                        single:true}
            }

            state.serviceLineDetails.procedures[rowId] = newData;

            /**
            * modifier validation done here
            */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
        },

        /**
         * update procedure other details
         * @param {*} state 
         * @param {*} actions 
         * @returns 
         */
        updateProcedureOtherDetails: (state, actions) => {
            let { serviceLineDetails, professionalClaimDetails } = current(state);
            let { value, field, rowId } = actions.payload;
            let newData = serviceLineDetails?.procedures[rowId];
            let formatted_value =null;
            let serviceFromDate = null;
            let serviceToDate = null;
            let professionClaimValue = "";
            if (value) {
                if (field == "charges" || field == "fee") {
                    formatted_value = String(value.trim())
                        .replace(/[^0-9.]/g, '')  // Remove all non-numeric and non-period characters
                        .replace(/(\..*?)\./g, '$1')  // Remove all extra decimal points
                        .replace(/(\.\d{2})\d*/g, '$1')  // Allow only two digits after the first decimal point
                        .substring(0, 9);  // Limit the length to 9 characters
                }
                else if (field == "fee_units") {
                    formatted_value = String(value.trim()).replace(/[^0-9]/g, '').substring(0, 9);
                }
                else if (field === "drug_code") {
                    formatted_value = String(value.trim())
                    .replace(/[^0-9-]/g, '') // Remove everything except numbers and hyphens
                    .replace(/-{2,}/g, '-')  // Replace multiple hyphens with a single hyphen
                    .replace(/^-/g, '')      // Remove leading hyphen if present
                    .substring(0, 14);
                } else if (field.endsWith("unit_value") || field === "anesthesia") {
                    formatted_value = String(value.trim()).replace(/^0|[^\d]/g, '').substring(0, 9);
                } else if (field === "dx_pointers") {
                    // Validate if user has selected a valid icd for the corresponding icd pointer
                    if (!validateIsValidDxPointer(serviceLineDetails, [...new Set(value.toUpperCase().replace(/[^A-L]/g, ''))].join(''))) {
                        alert("Invalid DX Pointer Combination.");
                        return;
                    }

                    formatted_value = [...new Set(value.toUpperCase().replace(/[^A-L]/g, ''))].join('');
                }
                else if (field == "dos_from" || field == "dos_to") {
                    formatted_value = format(value, "yyyy-MM-dd");
                } else {
                    formatted_value = value;
                }
                if (field === "responsibility_type") {
                    if (value == 5 && serviceLineDetails?.procedures[rowId].balance > 0) {
                        state.notifyMessage = i18n.t('errorMessages.paidErrorMessage');
                        state.showNotification = true;
                        state.statusTag = "error";
                        return
                    }

                    if (value == 5 && serviceLineDetails?.procedures[rowId].balance == undefined) {
                        state.notifyMessage = i18n.t('errorMessages.paidErrorMessage');
                        state.showNotification = true;
                        state.statusTag = "error";
                        return
                    }
                }

                if (field === "fee" || field === "fee_units") {
                    let pRow = serviceLineDetails?.procedures[rowId];
                    let feeValue = 0, feeUnitsValue = 0, chargeValue = 0;
                    if (field === "fee") {
                        feeValue = formatted_value;
                        feeUnitsValue = pRow.fee_units;
                    }
                    else if (field === "fee_units") {
                        feeValue = pRow.fee;
                        feeUnitsValue = value;
                    }
                    chargeValue = (decimalConverter(feeValue, 2) * decimalConverter(feeUnitsValue, 2));
                    chargeValue = isNaN(chargeValue) ? "0.00" : decimalConverter(chargeValue, 2);
                    newData = { ...newData, [field]: formatted_value, ["charges"]: chargeValue };
                }
                if (field == "dos_from") {
                    state.fee_field_status = {
                        all:true,
                        single:true}
                    newData = { ...newData, [field]: formatted_value, ["dos_to"]: formatted_value };
                    serviceFromDate = getServiceDateFrom(formatted_value, serviceLineDetails?.procedures, rowId);
                    serviceToDate = getServiceDateTo(formatted_value, serviceLineDetails?.procedures, rowId);
                    professionClaimValue = {
                        ...professionalClaimDetails,
                        "service_from_date": serviceFromDate ?? null,
                        "service_to_date": serviceToDate ?? null
                    }
                }
                if (field == "dos_to") {
                    state.fee_field_status = {
                        all:true,
                        single:true}
                    newData = { ...newData, ["dos_to"]: formatted_value };
                    serviceToDate = getServiceDateTo(formatted_value, serviceLineDetails?.procedures, rowId);
                    professionClaimValue = {
                        ...professionalClaimDetails,
                        "service_to_date": format(serviceToDate, "yyyy-MM-dd")
                    }
                }
                else {
                    newData = { ...newData, [field]: formatted_value };
                    if (field === "claim_status") {
                        newData = { ...newData, ["claim_sub_status"]: null }
                    }
                }

            } else if (!value) {
                newData = { ...newData, [field]: "" };
                if (field === "claim_status") {
                    newData = { ...newData, ["claim_sub_status"]: null }
                }
                if(field=="dos_from" || field=="dos_to"){
                   state.fee_field_status = {
                        all:true,
                        single:true}
                }
            }

            state.serviceLineDetails.procedures[rowId] = newData;

            if (professionClaimValue) {
                state.professionalClaimDetails = professionClaimValue;
            }

        },
        /**
         * function to change icd type to ICD1 or ICD2 in service line
         * @param {*} state 
         * @param {*} actions 
         */
        ICDTypeChange: (state, actions) => {
            let { serviceLineDetails } = current(state);
            let { field, value } = actions.payload;
            let updateValue = {
                ...serviceLineDetails,
                [field]: value ?? null
            }

            /**immidetly updating service line details */
            state.serviceLineDetails = updateValue;
        },
        /**
         * function to change diagnosis variables
         * @param {*} state 
         * @param {*} actions 
         */
        HandleICDChange: (state, actions) => {
            let {serviceLineDetails } = current(state);
            let { field, valueList } = actions.payload;
            // Validate if the request icd already in the state OR User trying to add icd item with blank slots in between
            let isIcdDuplicate = false;
            let allSlotValues = {};
            const labelToCheck = valueList[0]?.label ?? null;

            if (labelToCheck) {
                for (let i = 1; i <= 12; i++) {
                    const diagnosisKey = `diagnosis${i}`;

                    allSlotValues[diagnosisKey] = serviceLineDetails[diagnosisKey];

                    if (serviceLineDetails[diagnosisKey] === labelToCheck && diagnosisKey !== field) {
                        isIcdDuplicate = true;
                        break;
                    }
                }
            }

            allSlotValues[field] = labelToCheck;

            if (isIcdDuplicate || checkForGapsBetweenIcd(allSlotValues)) {
                const message = isIcdDuplicate
                    ? "You are trying to add an ICD that is already in the list."
                    : "Blank slots between ICDs are not allowed.";
                alert(message);
                return;
            }

            let label = ''
            if (Array.isArray(valueList) && valueList.length > 0) {
                label = valueList[0].label ?? null;
            }

            let updateValue = {
                ...serviceLineDetails,
                [field]: label ?? null, [field + "List"]: valueList
            }

            /**
             * when adding diganosis from history table creating dx pointer value and updating procedure
             */
            let dxPointer = updateDXpointer(updateValue);
            const updatedProcedures = updateValue?.procedures?.map((procedure) => ({
                ...procedure,
                dx_pointers: dxPointer
            }));

            updateValue = {
                ...updateValue,
                procedures: updatedProcedures
            }

            /**immidetly updating service line details */
            state.serviceLineDetails = updateValue;

            /**
             * diagnosis duplicate check and validation done here
             */
            let { dgnValid, isDuplicate, isValidted } = validateDiagnosis(updateValue, state.diagnosisValidation);

            state.diagnosisValidation = dgnValid;
            state.checkForDuplicatediagnosis = isDuplicate;
            state.diagnosisesValidated = isValidted;
        },

        /**
         * @description Add new procedure row item on add new btn click
         * @param {*} state 
         * @returns 
         */
        addNewProcedureRow: (state, actions) => {
            let { serviceLineDetails, billingInfoDetails,professionalClaimDetails,claimSubStatus} = current(state);
            let {paymentDone} = actions.payload;
            state.fee_field_status = {
                        all:true,
                        single:true}
            const getLastProcedureRowDos = (dos_type) => {
                const lastProcedure = serviceLineDetails?.procedures?.[0];
                const dos_from = lastProcedure?.dos_from;
                const dos_to = lastProcedure?.dos_to;

                if (dos_from && dos_to) {
                    return dos_type === 'dos_from' ? dos_from : dos_type === 'dos_to' ? dos_to : null;
                }
                return "";
            }

            const getDxPointers = () => {
                let dxPointers = "";
                const pointerMapping = {
                    "1": "A", "2": "B", "3": "C", "4": "D", "5": "E", "6": "F",
                    "7": "G", "8": "H", "9": "I", "10": "J", "11": "K", "12": "L"
                };

                for (let item = 1; item <= 12; item++) {
                    const diagnosisKey = `diagnosis${item}`;
                    if (serviceLineDetails?.[diagnosisKey]) {
                        dxPointers += pointerMapping[item] || "";
                    }
                }

                return dxPointers;
            }

            const getResponsibility = () => {
                if (!billingInfoDetails?.payerInsuranceList || billingInfoDetails?.payerInsuranceList.length === 0) {
                    return RESPONSIBILITY_TYPES.find(item => item.name === "Patient")?.id || "";
                }

                const sortedList = billingInfoDetails?.payerInsuranceList?.slice().sort((a, b) => a.priority - b.priority);
                return Number(sortedList[0]?.priority) || "";
            }

            const getClaimStatusAndSubStatus = () => ({
                claim_status: paymentDone ? null : professionalClaimDetails?.claim_status_id,
                claim_sub_status: paymentDone ? null : professionalClaimDetails?.claim_sub_status_id,
            });

            state.serviceLineDetails.procedures = [...serviceLineDetails.procedures, {
                ...newProcedureRowItem,
                id: `New-${crypto?.randomUUID()}`,
                dos_from: getLastProcedureRowDos('dos_from'),
                dos_to: getLastProcedureRowDos('dos_to'),
                dx_pointers: getDxPointers(),
                responsibility_type: getResponsibility(),
                ...getClaimStatusAndSubStatus()
            }]


            if (claimSubStatus.length > 0 && professionalClaimDetails.claim_status) {
                let filterData = claimSubStatus?.filter(item => item.parent_claim_status_ids?.includes(professionalClaimDetails.claim_status));
                state.serviceLineDetails.claim_sub_status[serviceLineDetails.procedures.length] = filterData;
            }

            /**
             * modifier validating
             */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
            // Once the new procedure row is updated to the state move the scroll position to the newly added procedure row
            setTimeout(() => {
                document.getElementById(`procedure_row_${serviceLineDetails?.procedures[serviceLineDetails?.procedures.length - 1]?.id}`)?.scrollIntoView({
                    behavior: 'smooth'
                });
            }, 200);
        },

        /**
         * @description Remove a procedure row item from the procedure rows
         * @param {*} state 
         * @returns 
         */
        removeProcedureRowItem: (state, actions) => {
            let { serviceLineDetails } = current(state);
            let { index, procedureId,payment_done } = actions.payload;
            if(payment_done){
                state.deleteProcedureModal=true;
                state.deleteProcedure={
                    procedureId:procedureId,
                    procedureIndex:index
                };
            }else{
            // Check if the procedure ID matches and remove the item using filter
            if (serviceLineDetails.procedures[index]?.id === procedureId) {
                serviceLineDetails = {
                    ...serviceLineDetails,
                    claim_sub_status: {
                        ...serviceLineDetails.claim_sub_status,
                        [index]: [] // Correctly update the specific index of claim_sub_status
                    },
                    procedures: serviceLineDetails.procedures.filter((_, i) => i !== index)
                };
            }

            state.serviceLineDetails = serviceLineDetails;

            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
            state.deleteProcedureModal=false;
            state.deleteProcedure=initialState.deleteProcedure;
            state.fee_field_status=initialState.fee_field_status;
            }
        },

        /**
         * show error message
         * @param {*} state 
         * @param {*} actions 
         */
        showNotifications: (state, actions) => {
            state.notifyMessage = actions.payload.message;
            state.showNotification = actions.payload.isopen;
            state.statusTag = actions.payload.status;
        },

        /**
         *show modal windows 
         * @param {s} state 
         * @param {*} actions 
         */
        showModalWindow: (state, actions) => {
            let { key, value, claimPK, claim_stages,close_window=false } = actions.payload;
            let { professionalClaimDetails } = current(state);
            state[key] = actions.payload.value;

            if (key == "requestSupportOpen" && value == false) {
                state.selectedStageIds = [],
                state.submitting = false;
                state.RequestDialogDetails = {
                    reasonId: "",
                    explain: "",
                    role: ROLE_MANAGER,
                    assignTo: ""
                }
            }

            if(key=="openAuditReviewModal" && value==false){
                state.openAuditReviewModal=false;
                state.masterWorkflowList=initialState.masterWorkflowList;
                state.workflowDetails=initialState.workflowDetails;
                state.auditorIssuesList=initialState.auditorIssuesList;
                state.editingIssueId=initialState.editingIssueId;
                state.auditReviewDetails=initialState.auditReviewDetails;
            }

            if (key == "openReviewResolveModal" && value == false) {
                state.selectedStageIds = [],
                state.deEscalationDetails={
                    remarks:"",
                    assignTo:""
                }
                state.RequestDialogDetails = {
                    reasonId: "",
                    explain: "",
                    role: ROLE_MANAGER,
                    assignTo: ""
                }
            }

            if (key === "openCompleteClaimModal") {
                const isOpen = !!value;
                state.openCompleteClaimModal = isOpen;
                state.claim_stage_ids_clicked = [];
                state.claim_stages = isOpen ? (Array.isArray(claim_stages) ? claim_stages : []) : [];
            }

            if (key === "patientAlertModal" && value === false) {
                state.alertMessage = ""
            }

            if (key === "deleteProcedureModal" && value === false) {
                state.deleteProcedure = initialState.deleteProcedure
            }

            if(close_window){
                state.isClaimClose=true;
            }

            if (key === "inactiveClaimModal" && value === false && claimPK) {
                /**on inactive modal hide need to change inactive to false and active to true */
                let updateValue = {
                    ...professionalClaimDetails,
                    "claim_inactive": false,
                    "active": true
                }

                /**
             * updating profession claim details immediately
             */
                state.professionalClaimDetails = updateValue;

            }

            if (key === "activeClaimModal" && value === false) {
                let updateValue = {
                    ...professionalClaimDetails,
                    "claim_inactive": true,
                    "active": false
                }

                /**
             * updating profession claim details immediately
             */
                state.professionalClaimDetails = updateValue;
            }

            if (key === "releaseHoldModal") {
                state.releaseModal.isOpen = value;
                if (!value) {
                    state.releaseModal.reason = ""; // Auto-clear reason when closing
                }
            } else if (key === "openHoldModal") {
                state.holdModal = value 
                    ? { ...state.holdModal, isOpen: true }
                    : { isOpen: false, reason: "",option:[],status:""};
                if (value) {
                    state.releaseModal.isOpen = false; // Prevents release modal from showing simultaneously
                }
            } else if (key === "statusUpdateModal") {
                state.ui.statusUpdateModalOpen = value;
            }
        },
        /**
         * if save or save close have waring pop it need to manage from main component thats why we are storing claim details from the save response
         * @param {*} state 
         * @param {*} action 
         */
        saveClaimIDWaringPopUP: (state, action) => {
            let item = {
                "claimId": action.payload.claimId,
                "claimPK": action.payload.claimPK,
                "isClose": action.payload.isClose,
                "isSave": action.payload.isSave
            }

            state.savedClaimDetails = item;
        },
        /**
         * SHOW FORM WARNING MESSAGE ON SAVE 
         * @param {*} state 
         * @param {*} actions 
         */
        setFormWarningMessages: (state, actions) => {
            state.formWarningMessage = actions.payload;
        },
        /**
         * SHOW FROM WARNING MESSAGE ON TOP OF THE COMPONENT
         * @param {*} state 
         * @param {*} actions 
         */
        setFormWaringData: (state, actions) => {
            state.formWarningData = actions.payload;
        },
        /**
         * to reset error showing varibale to null after show the error
         * @param {*} state 
         */
        resetNotifyWindow: (state) => {
            state.notifyMessage = "";
            state.showNotification = false;
            state.statusTag = "";
            state.isClaimClose=false;
            state.escalationResultComplete=false;
        },

        setActiveSubTab: (state, actions) => {
            state.activeSubTab = actions.payload;
        },

        setClaimStageIdsClicked: (state, action) => {
            state.claim_stage_ids_clicked = Array.isArray(action.payload) ? action.payload : [];
        },

        /**
         * remove claim details of seleted or existing patient
         * @param {*} state 
         * @param {*} action 
         */
        removeClaimDetails: (state, action) => {
            let claim_pk = action.payload ? action.payload : "add_new";
            state.CallInsurance = initialState.CallInsurance;
            state.claimPK = "";
            state.claim_pk = "";
            state.diagnosis_pk = "";
            state.billing_info_pk = "";
            state.addition_info_pk = "";
            state.patient_PK = "";
            state.professionalClaimDetails = initialState.professionalClaimDetails;
            state.billingInfoDetails = initialState.billingInfoDetails;
            state.serviceLineDetails = initialState.serviceLineDetails;
            state.diagnosisValidation = initialState.diagnosisValidation;
            state.additionalClaimDetails = initialState.additionalClaimDetails;
            state.payerInsuranceList = [];
            state.primaryInsuranceDataList = [];
            state.secondaryInsuranceDataList = [];
            state.tertiaryInsuranceDataList = [];
            state.primarypolicyId = "";
            state.insurancePrimary = "";
            state.secondarypolicyId = "";
            state.insurancesecondary = "";
            state.tertiarypolicyId = "";
            state.insuranceTertiary = "";
            state.ModifiersValidated = [];
            state.inputM1Validated = [];
            state.inputM2Validated = [];
            state.inputM3Validated = [];
            state.inputM4Validated = [];
            state.checkForDuplicatediagnosis = false;
            state.diagnosisesValidated = true;
            state.infoLoaded = false;
            state.patientSelectedData = [];
            state.patientAdvanceSearchData = [];
            state.selectedPatient = "";
            state.insuranceCompleted = false;
            state.formWarningMessage = "";
            state.formWarningData = [];
            state.formWarningStatus = false;
            state.activeSubTab = TAB1

            let index = state.open_pks.findIndex((item) => item == claim_pk);
            state.open_pks.splice(index, 1);
            let pksIndex = state.pks.findIndex((item) => item.claim_pk == claim_pk);
            state.pks.splice(pksIndex, 1);

        },

        /**
         * getting claim details
         * @param {*} state 
         * @param {*} actions 
         */
        getClaimDetailsByClaimPK: (state, actions) => {
            let claim_pk = actions.payload ? actions.payload : "add_new";
            let { professionalClaim, billingInfo, serviceLineData, additionalClaimData } = current(state);
            let professionalDetails = professionalClaim.filter((item) => item.claim_pk == claim_pk);
            let billingDetails = billingInfo.filter((item) => item.claim_pk == claim_pk);
            let serviceDetails = serviceLineData.filter((item) => item.claim_pk == claim_pk);
            let additionalDetails = additionalClaimData.filter((item) => item.claim_pk == claim_pk);
            /**setting to single state */
            state.professionalClaimDetails = professionalDetails.length > 0 ? professionalDetails[0] : initialState.professionalClaimDetails;
            state.billingInfoDetails = billingDetails.length > 0 ? billingDetails[0] : initialState.billingInfoDetails;
            state.serviceLineDetails = serviceDetails.length > 0 ? serviceDetails[0] : initialState.serviceLineDetails;
            state.additionalClaimDetails = additionalDetails.length > 0 ? additionalDetails[0] : initialState.additionalClaimDetails;
            state.claimPK = actions.payload ? actions.payload : "";
            state.claim_pk = actions.payload ? actions.payload : "";
            state.diagnosis_pk = professionalDetails.length > 0 ? professionalDetails[0].pks.diagnosises_pk : "";
            state.billing_info_pk = professionalDetails.length > 0 ? professionalDetails[0].pks.billing_info_pk : "";
            state.addition_info_pk = professionalDetails.length > 0 ? professionalDetails[0].pks.additional_claim_info_pk : "";
            state.patient_PK = professionalDetails.length > 0 ? professionalDetails[0].patient : "";

            /**
          * modifier validation done here
          */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4
            } = validateModifier(serviceDetails.length > 0 ? serviceDetails[0].procedures : []);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;

            /**
            * setting form waring error on popup
            */
            let serverWarings = setWarningFromServer(professionalDetails.length > 0 ? professionalDetails[0] : {});
            state.formWarningData = serverWarings;

            /**
            * diagnosis duplicate check and validation done here
            */
            let { dgnValid, isDuplicate, isValidted } = validateDiagnosis(serviceDetails.length > 0 ? serviceDetails[0] : initialState.serviceLineDetails, state.diagnosisValidation);

            state.diagnosisValidation = dgnValid;
            state.checkForDuplicatediagnosis = isDuplicate;
            state.diagnosisesValidated = isValidted;
        },
        /**
         * adding new diganosis variable from patient history table
         * @param {*} state 
         * @param {*} action 
         */
        addDiganosisVariablePatientHistory: (state, action) => {
            let { item } = action.payload;
            let { serviceLineDetails } = current(state);
            let icdToAdd = { label: item[0].id, name: item[0].value }
            let availableSlotInICDList = null;
            for (let i = 1; i <= 12; i++) {
                const key = `diagnosis${i}`;
                if (serviceLineDetails?.[key] === "") {
                    availableSlotInICDList = key;
                    break;
                }
            }

            let updateValue = {
                ...serviceLineDetails,
                [availableSlotInICDList]: icdToAdd.label.toString(),
                [availableSlotInICDList + 'List']: [icdToAdd]
            }

            /**
             * when adding diganosis from history table creating dx pointer value and updating procedure
             */
            const dxPointers = updateDXpointer(updateValue);
            const updatedProcedures = updateValue?.procedures?.map((procedure) => ({
                ...procedure,
                dx_pointers: dxPointers
            }));

            updateValue = {
                ...updateValue,
                procedures: updatedProcedures
            }

            /**immidetly updating service line details */
            state.serviceLineDetails = updateValue;

            /**
             * diagnosis duplicate check and validation done here
             */
            let { dgnValid, isDuplicate, isValidted } = validateDiagnosis(updateValue, state.diagnosisValidation);

            state.diagnosisValidation = dgnValid;
            state.checkForDuplicatediagnosis = isDuplicate;
            state.diagnosisesValidated = isValidted;
        },
        /**
         * removing existing or added diganosis variable from patient history table
         * @param {*} state 
         * @param {*} action 
         */
        removeDiganosisVariablePatientHistory: (state, action) => {
            let { item } = action.payload;
            let { serviceLineDetails } = current(state);
            let removedKey = null;
            for (let i = 1; i <= 12; i++) {
                const key = `diagnosis${i}`;
                if (serviceLineDetails[key] == item[0].id) {
                    removedKey = key;
                    break;
                }
            }
            // remove the item from the state
            let updateValue = {
                ...serviceLineDetails,
                [removedKey]: '',
                [removedKey + 'List']: []
            }

            // check if any key has value that comes after the removed icd/dx index
            function findKeysAfterTheRemovedItem(obj, removedKey) {
                let keys = Object.keys(obj);
                let index = keys.indexOf(removedKey);
                let result = [];
                for (let i = index; i < keys.length; i++) {
                    if (obj[keys[i]] !== null && obj[keys[i]] !== '' && (Array.isArray(obj[keys[i]]) ? obj[keys[i]].length : true)) {
                        result.push(keys[i]);
                    }
                }
                return result;
            }
            const icdItemComesAfterRemovedSlot = findKeysAfterTheRemovedItem(updateValue, removedKey);
            // shift all the icds to the unoccupied slot in the list
            if (icdItemComesAfterRemovedSlot.length) {
                icdItemComesAfterRemovedSlot.forEach((icdToSwitchSlot) => {
                    if (icdToSwitchSlot.startsWith('diagnosis') && icdToSwitchSlot.length <= 11) {
                        let availableSlotInICDList1 = null;
                        for (let i = 1; i <= 12; i++) {
                            const key = `diagnosis${i}`;
                            if (updateValue[key] === "") {
                                availableSlotInICDList1 = key;
                                break;
                            }
                        }
                        let tempStoreICDToShift = updateValue[icdToSwitchSlot]
                        let tempStoreICDListToShift = updateValue[`${icdToSwitchSlot}List`];
                        //shift the values
                        updateValue = {
                            ...updateValue,
                            [availableSlotInICDList1]: tempStoreICDToShift.toString(),
                            [availableSlotInICDList1 + 'List']: tempStoreICDListToShift
                        }

                        // remove the shifted value
                        updateValue = {
                            ...updateValue,
                            [icdToSwitchSlot]: '',
                            [`${icdToSwitchSlot}List`]: []
                        }
                    }
                })
            }

            /**
             * when removing
             *  diganosis from history table creating dx pointer value and updating procedure
             */
            const dxPointers = updateDXpointer(updateValue);
            const updatedProcedures = updateValue?.procedures?.map((procedure) => ({
                ...procedure,
                dx_pointers: dxPointers
            }));

            updateValue = {
                ...updateValue,
                procedures: updatedProcedures
            }

            /**immidetly updating service line details */
            state.serviceLineDetails = updateValue;

            /**
             * diagnosis duplicate check and validation done here
             */
            let { dgnValid, isDuplicate, isValidted } = validateDiagnosis(updateValue, state.diagnosisValidation);

            state.diagnosisValidation = dgnValid;
            state.checkForDuplicatediagnosis = isDuplicate;
            state.diagnosisesValidated = isValidted;

        },

        /**
         * add cpt from patient history table
         * @param {*} state 
         * @param {*} action 
         */
        addCPTFrompatientHistoryTable: (state, action) => {
            let { item, paymentDone } = action.payload;
            let { patientHistoryCPT, serviceLineDetails, billingInfoDetails,professionalClaimDetails,claimSubStatus} = current(state);
            let tmpProcedure = { ...procedure };
            const selectedCPT = patientHistoryCPT.filter(ele => ele.id == item[0].id);

            const getLastProcedureRowDos = (dos_type) => {
                const lastProcedure = serviceLineDetails?.procedures?.[serviceLineDetails?.procedures.length - 1];
                const dos_from = lastProcedure?.dos_from;
                const dos_to = lastProcedure?.dos_to;

                if (dos_from && dos_to) {
                    return dos_type === 'dos_from' ? dos_from : dos_type === 'dos_to' ? dos_to : "";
                }
                return "";
            }

            const getResponsibility = () => {
                if (!billingInfoDetails?.payerInsuranceList || billingInfoDetails?.payerInsuranceList.length === 0) {
                    return RESPONSIBILITY_TYPES.find(item => item.name === "Patient")?.id || "";
                }

                const sortedList = billingInfoDetails?.payerInsuranceList?.slice().sort((a, b) => a.priority - b.priority);
                return Number(sortedList[0]?.priority) || "";
            }

            if (selectedCPT.length > 0) {
                if (selectedCPT[0].id) {
                    tmpProcedure.cpt_hcpcs = selectedCPT[0]?.id;
                    tmpProcedure.cpt_hcpcsList = [{ id: selectedCPT[0]?.id, name: selectedCPT[0]?.name }];
                } else {
                    tmpProcedure.cpt_hcpcs = "";
                    tmpProcedure.cpt_hcpcsList = [];
                }

                if (selectedCPT[0].pos) {
                    tmpProcedure.pos_id = selectedCPT[0]?.pos?.id ? selectedCPT[0]?.pos?.id : "";
                    tmpProcedure.pos_id_List = selectedCPT[0]?.pos?.id ? [selectedCPT[0]?.pos] : [];
                } else {
                    tmpProcedure.pos_id = "";
                    tmpProcedure.pos_id_List = [];
                }

                tmpProcedure.fee_units = selectedCPT[0]?.unit ? selectedCPT[0]?.unit : 0;
                tmpProcedure.fee = selectedCPT[0]?.fee ? decimalConverter(selectedCPT[0]?.fee, 2) : "0.00";
                tmpProcedure.charges =
                    decimalConverter(selectedCPT[0]?.fee ? selectedCPT[0]?.fee : 0, 2) *
                    parseInt(selectedCPT[0]?.unit ? selectedCPT[0]?.unit : 0);
            }

            const getClaimStatusAndSubStatus = () => ({
                claim_status: paymentDone ? null : professionalClaimDetails?.claim_status,
                claim_sub_status: paymentDone ? null : professionalClaimDetails?.claim_sub_status,
            });

            const getDxPointers = () => {
                let dxPointers = "";
                const pointerMapping = {
                    "1": "A", "2": "B", "3": "C", "4": "D", "5": "E", "6": "F",
                    "7": "G", "8": "H", "9": "I", "10": "J", "11": "K", "12": "L"
                };

                for (let item = 1; item <= 12; item++) {
                    const diagnosisKey = `diagnosis${item}`;
                    if (serviceLineDetails?.[diagnosisKey]) {
                        dxPointers += pointerMapping[item] || "";
                    }
                }

                return dxPointers;
            }

            state.serviceLineDetails.procedures = [...serviceLineDetails.procedures, {
                ...tmpProcedure,
                id: `New-${crypto?.randomUUID()}`,
                dos_from: getLastProcedureRowDos('dos_from'),
                dos_to: getLastProcedureRowDos('dos_to'),
                dx_pointers: getDxPointers(),
                responsibility_type: getResponsibility(),
                ...getClaimStatusAndSubStatus()

            }]


            if (claimSubStatus.length > 0 && professionalClaimDetails.claim_status) {
                let filterData = claimSubStatus?.filter(item => item.parent_claim_status_ids?.includes(professionalClaimDetails.claim_status));
                state.serviceLineDetails.claim_sub_status[serviceLineDetails.procedures.length] = filterData;
            }

            /**
             * modifier validating
             */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
            // Once the new procedure row is updated to the state move the scroll position to the newly added procedure row
            setTimeout(() => {
                document.getElementById(`procedure_row_${serviceLineDetails?.procedures[serviceLineDetails?.procedures.length - 1]?.id}`)?.scrollIntoView({
                    behavior: 'smooth'
                });
            }, 200);

            state.fee_field_status={
                all:true,
                single:true
            }

        },
        /**
         * When a claim get un-locked from page lock feature, and if that claim is currently in the users session, then below
         * reducer func handles the logic of un-locking that particular claim
         * @param {*} state 
         * @param {*} action contains the claimPk 
         */
        handleClaimPageUnlock: (state, action) => {
            let { professionalClaimDetails } = current(state);
            const claimPk = action.payload?.claimPK;
            if (claimPk) {
                let updateValue = {
                    ...professionalClaimDetails,
                    locked_by_name: "",
                    locked: false,
                    claim_editable: true
                }
                /**
                 * updating profession claim details immediately
                 */
                state.professionalClaimDetails = updateValue;
            }
        },

        filterClaimSubStatus: (state, actions) => {
            let { claimSubStatus } = current(state);
            let { value, propindex } = actions.payload;

            if (claimSubStatus.length > 0) {
                let filterData = claimSubStatus?.filter(item => item.parent_claim_status_ids?.includes(value));
                state.serviceLineDetails.claim_sub_status[propindex] = filterData;
            }
        },
        changeActiveParentTab:(state,action)=>{
            state.activeParentTab=action.payload;
        },
        openViewActivity: (state, action) => {
            state.viewActivity.isOpen = true;
            state.viewActivity.claimPK = action.payload;
            state.viewActivity.activeTab = 0;
        },
        closeViewActivity: (state) => {
            state.viewActivity.isOpen = false;
            state.viewActivity.claimPK = null;
            state.viewActivity.activeTab = 0;
            state.viewActivity.workflowData = [];
            state.viewActivity.logsData = [];
            state.viewActivity.commentsData = [];
            state.viewActivity.error = null;
        },
        setActivityTab: (state, action) => {
            state.viewActivity.activeTab = action.payload;
        },
        addActivityComment: (state, action) => {
            state.viewActivity.commentsData.push(action.payload);
        },
        /**
         * Handle toggle void switch 
         * @param {*} state 
         * @param {*} action 
         */
        onVoidClick: (state, action) => {
            const { id } = action.payload;
            const { serviceLineDetails } = state;
        
            const firstGroupProcedures = serviceLineDetails?.procedures;
            const targetData = firstGroupProcedures?.find(p => p.id === id);
            const targetDetail = serviceLineDetails?.procedures?.find(p => p.id === id);
        
            if (!targetData || !targetDetail) return;
        
            const canVoid = targetData.charges == targetData.balance;
            if (canVoid) {
                state.selectedVoidId = id;
                state.voidToggleModal = {
                    show: true,
                    isVoiding: !targetDetail.voided
                };
            } else {
                state.showNotification = true;
                state.notifyMessage = i18n.t("errorMessages.voidAlert");
                state.statusTag = 'error';
            }
        },

        /**
         * Confirm void/unvoid action - toggles voided state based on isVoiding flag
         * @param {*} state 
         */
        confirmVoidToggle: (state) => {
            const { selectedVoidId, serviceLineDetails, voidToggleModal } = state;
            const targetDetail = serviceLineDetails?.procedures?.find(p => p.id === selectedVoidId);
            
            if (targetDetail) {
                targetDetail.voided = voidToggleModal.isVoiding;
            }
            state.voidToggleModal.show = false;
        },

        /**
         * Cancel void/unvoid action - closes modal without changes
         * @param {*} state 
         */
        cancelVoidToggle: (state) => {
            state.voidToggleModal.show = false;
        },
        showClaimBetaNotifyWindow: (state, actions) => {
            state.notifyMessage = actions.payload.message;
            state.showNotification = true;
            state.statusTag = actions.payload.status
        },

        escalationDetailsChange:(state,actions)=>{
            let {key,value}=actions.payload;

            state.deEscalationDetails[key]=value;
        },

        setAssignToList: (state, actions) => {
            let { assignToList, currentEscalation } = actions.payload;
            if (currentEscalation) {
                state.deEscalationDetails.assignTo = currentEscalation.assign_back_to || ''
            } else if (assignToList && assignToList?.length > 0 && !state?.deEscalationDetails?.assignTo) {
                state.deEscalationDetails.assignTo = assignToList[0].id || ''
            }
        },
        setSelectedStageIds: (state, action) => { 
            let {array,activeTab}=action.payload
            state.selectedStageIds = array;
            state.RequestDialogDetails[activeTab]= {
                ...state.RequestDialogDetails[activeTab],
                stageIds:array
            }
        },

        RequestDialogChange: (state, action) => {
            let { tab, key, value } = action.payload;
            if (tab) {
                // Ensure RequestDialogDetails exists
                if (!state.RequestDialogDetails) {
                    state.RequestDialogDetails = {};
                }
                // If the specific tab (client/manager) is missing, initialize it
                if (!state.RequestDialogDetails[tab]) {
                    state.RequestDialogDetails[tab] = { stageIds: [], reasonId: [], explain: "" };
                }
                state.RequestDialogDetails[tab][key] = value;
            }
            // If no tab is provided
            else {
                if (!state.RequestDialogDetails) {
                    state.RequestDialogDetails = {};
                }
                state.RequestDialogDetails[key] = value;
            }
        },
        
        setClaimIdOpenInNewTab: (state) => {
            state.claim_id_open_in_new_tab = "";
            state.makeInProgressStatus = false;
        },
        updateArNotesField: (state, action) => {
            const { field, value } = action.payload;

            if (!state.arNotes) return;

            // Direct update for the target field
            state.arNotes[field] = value;

            // 1. If a template is selected, auto-populate the notes field
            if (field === "selected_template_id") {
                const selectedTemplate = state.arNotes.follow_up_notes?.find(
                    item => String(item.id) === String(value)
                );
                if (selectedTemplate) {
                    state.arNotes.notes = selectedTemplate.template_content || selectedTemplate.name || "";
                }
            }

            // 2. Auto-calculate follow-up date
            if (field === 'ar_action_code' && value) {
                const actionList = state.dropDownListData?.arActionCodeList || [];
                
                const selectedAction = actionList.find(
                    (a) => String(a.id) === String(value) || String(a.value) === String(value) || String(a.code) === String(value)
                );

                if (selectedAction) {
                    const rawDays = selectedAction.follow_up_days_count ?? selectedAction.follow_up_days ?? selectedAction.days;
                    const daysToAdd = (rawDays !== null && rawDays !== undefined && !isNaN(Number(rawDays)) && Number(rawDays) >= 0) ? Number(rawDays) : 30;
                    
                    const calculatedDate = format(addDays(new Date(), daysToAdd), 'yyyy-MM-dd');
                    state.arNotes.next_followup_date = calculatedDate;
                }
            }
        },
        clearArNotes: (state) => {
            state.arNotes.id = "";
            state.arNotes.notes = "";
            state.arNotes.ar_status_code = "";
            state.arNotes.ar_action_code = "";
            state.arNotes.next_followup_date = "";
            state.arNotes.selected_template_id = "";
            state.arNotes.original_ar_notes = null;
            state.arNotes.loading = false;        
        },

        cancelEditNotes: (state) => {
            if (state.original_ar_notes) {
                // Overwrite current values with the pristine API snapshot data
                state.arNotes.notes = state.original_ar_notes.notes;
                state.arNotes.ar_status_code = state.original_ar_notes.ar_status_code;
                state.arNotes.ar_action_code = state.original_ar_notes.ar_action_code;
                state.arNotes.next_followup_date = state.original_ar_notes.next_followup_date;
                state.arNotes.selected_template_id = state.original_ar_notes.selected_template_id;
            }
        },
        setActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        setAuditDetails: (state, action) => {
            let {key,value}=action.payload
            state.auditReviewDetails={
                ...state.auditReviewDetails,
                [key]:value
            }
        },
        handleEditIssueClick:(state,action)=>{
            state.editingIssueId=action.payload?.id;
            const cleanEditDesc = action.payload?.description ? action.payload?.description.replace(/\[WF_TRACK:\d+\]/, '').trim() : "";
            const savedReasons = Array.isArray(action.payload?.reason_id) ? action.payload?.reason_id : [];
            const savedFields = Array.isArray(action.payload?.field_group_id) ? action.payload?.field_group_id : [];

            state.auditReviewDetails={
                field: savedFields,
                exceptionType:savedReasons,
                comments:cleanEditDesc
            }
        },
        setHoldDetails: (state, action) => {
            const { key, value } = action.payload;
            const finalValue = (key === 'reason' && typeof value === 'string')
                ? value.replace(/\s/g, '')
                : value;
            if (state.holdModal) {
                state.holdModal[key] = finalValue;
            } else {
                state[key] = finalValue;
            }
        },
        setSelectedClaimIDs: (state, action) => {
            state.selection.claimIDs = action.payload || [];
        },
        setCheckedAllClaim: (state, action) => {
            state.selection.isAllChecked = action.payload;
        },
        setReleaseDetails: (state, action) => {
            const { key, value } = action.payload;
            if(key === 'reason' && /\s/.test(value)){
                return
            }
            state.releaseModal[key] = value;
        },
        
    },
    extraReducers: (builder) => {
        /************************************************************ */
        builder
            .addCase(getSearchClaimsTableData.pending, (state) => {
                state.dataLoading = true;
            })

        builder
            .addCase(getSearchClaimsTableData.fulfilled, (state, action) => {
                state.dataLoading = false
                state.searchClaimsTable.tableData = action.payload?.data?.results ?? [];
                state.searchClaimsTable.totalPages = Math.ceil(action.payload?.data?.count / action.payload?.data?.page_size) ?? 0;
                state.searchClaimsTable.page = action.payload.isSearch ? 1 : action.payload?.data?.links?.current_page;
            })

        builder
            .addCase(getSearchClaimsTableData.rejected, (state, action) => {
                state.dataLoading = false;
                state.apiError = action.payload?.error_message ?? i18n.t('claim_beta.api_errors.error_fetching_data');
            })
        /*****************************/

        /**
         * new claim all save details handled here
         */

        builder.addCase(getPatientDetails.pending, (state) => {
            state.dataLoading = true;
        })

        builder.addCase(getPatientDetails.fulfilled, (state, action) => {
            state.dataLoading = false;
            const {
                billingData,
                serviceData,
                additionalData,
                practiceData,
                accidentList,
                responsibilityType,
            } = action.payload;

            /**
             * billing info data is processed here
             */

            let {billingTabData,insuranceCompleted} = generateBillingTabSaveData(billingData,"");
            billingTabData = {
                ...billingTabData,
                claim_pk: "add_new",
            }

            state.insuranceCompleted=insuranceCompleted;

            /**
             * storing data to state for billing info
             */
            state.billingInfo.push(billingTabData);
            state.billingInfoDetails = billingTabData;

            /**state setting to true to call payer insurance */
            state.CallInsurance = true;

            /**serviceline data processed here */
            let claim_sub_status_list = [];
            let serviceLineTabData = generateServiceLineSaveData(serviceData);
            serviceLineTabData = {
                ...serviceLineTabData,
                responsibility_type: responsibilityType,
                claim_status: [],
                claim_sub_status: claim_sub_status_list,
                claim_pk: "add_new"
            }

            /**
             * modifier validation done here
             */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(serviceLineTabData?.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;

            /**
             * storing serviceLine details to state
             */

            state.serviceLineData.push(serviceLineTabData);
            state.serviceLineDetails = serviceLineTabData;

            /**
             * additional claim data is processed here
             */

            let additionalTabData = generateAdditionalclaimSaveData(additionalData, practiceData, accidentList)
            additionalTabData = {
                ...additionalTabData,
                claim_pk: "add_new"
            }

            /**
            * storing additional claim details to state
            */

            state.additionalClaimData.push(additionalTabData);
            state.additionalClaimDetails = additionalTabData;

            /**pusing add_new to open_pks */
            state.open_pks.push("add_new");

        })

        builder.addCase(getPatientDetails.rejected, (state, action) => {
            state.notifyMessage = action.payload;
            state.showNotification = true;
            state.statusTag = "error";
            state.dataLoading = false;
        })

        //get patient additional details pending state
        builder.addCase(getEditDetails.pending, (state) => {
            state.editDetailsLoading = true;
        })
        //get patient additional data api success response handled here
        builder.addCase(getEditDetails.fulfilled, (state, actions) => {
            let { claimInfo,
                billingInfo,
                procedureInfo,
                additionalInfo,
                accidentList,
                responsibilityType,
                payload } = actions.payload
            state.diagnosis_pk = claimInfo?.pks?.diagnosises_pk;
            state.billing_info_pk = claimInfo?.pks?.billing_info_pk,
                state.addition_info_pk = claimInfo?.pks?.additional_claim_info_pk
            state.CallInsurance = true;
            state.patient_PK = claimInfo?.patient ?? ""
            state.active_escalation = claimInfo?.active_escalation
            /**
             * parent component claim info data processed here
             */
            let parentComponentData = generateClaimCommonUpdateData(claimInfo, payload)
            let claimStages=claimStagesForEscalateDropdown(claimInfo);
            state.stageOptions=(Array.isArray(claimStages) ? claimStages.map((s) => ({ id: s.id, name: s.label })) : []);
            state.reportsTo=reportsToDisplay(claimInfo)
            let {field_claiminfo,field_billinginfo,fieldAddNotInfo,fieldServiceInfo,fieldAdditionalInfo}=FieldPermissionGenerator(claimInfo?.permitted_fields ?? {})
            state.claimPK = parentComponentData ? parentComponentData.id : "";

            parentComponentData = {
                ...parentComponentData,
                permittedFields:{...field_claiminfo,...fieldAddNotInfo},
                claim_pk: payload
            }

            state.professionalClaimDetails = parentComponentData;
            state.duplicateDataSet.professionalClaimDetails = parentComponentData;

            let {billingInfoTabData,payerInfoData,insuranceCompleted} = generateBillingInfoUpdateData(billingInfo,payload);
            billingInfoTabData = {
                ...billingInfoTabData,
                ...payerInfoData,
                payer_info:billingInfoTabData.payer_info ? payerInfoData.payer_info : [],
                payerInsuranceList: payerInfoData.payerInsuranceList ? payerInfoData.payerInsuranceList : []
            }
            
            state.insuranceCompleted=insuranceCompleted;
            /**
             * patient payer info for existing data is handled here
             */
         if (billingInfo && Array.isArray(billingInfo.payer_info)) {
            billingInfo?.payer_info?.forEach(item => {
                let pIdValue;
                if (item.priority === 1) {
                    pIdValue = findPId(billingInfoTabData.primaryInsuranceDataList || [], item.policy_id,billingInfo.payer_info);
                    billingInfoTabData = {
                        ...billingInfoTabData,
                        insurancePrimary: pIdValue,
                        primarypolicyId: item.policy_id,
                        primaryPayerId: item.ins_payer_id
                    }
                } else if (item.priority === 2) {
                    pIdValue = findPId(billingInfoTabData.secondaryInsuranceDataList || [], item.policy_id,billingInfo.payer_info);
                    billingInfoTabData = {
                        ...billingInfoTabData,
                        insurancesecondary: pIdValue,
                        secondarypolicyId: item.policy_id,
                        secondaryPayerId: item.ins_payer_id
                    }
                } else if (item.priority === 3) {
                    pIdValue = findPId(billingInfoTabData.tertiaryInsuranceDataList || [], item.policy_id,billingInfo.payer_info)
                    billingInfoTabData = {
                        ...billingInfoTabData,
                        insuranceTertiary: pIdValue,
                        tertiarypolicyId: item.policy_id,
                        tertiaryPayerId: item.ins_payer_id
                    }
                }
            })
            }
            billingInfoTabData = {
                ...billingInfoTabData,
                permittedFields:field_billinginfo,
                claim_pk: payload,
                ...payerInfoData
            }


            state.billingInfoDetails = billingInfoTabData;
            state.duplicateDataSet.billingInfoDetails = billingInfoTabData;

            /**
             * serviceLine tab data processed here
             */
            let claim_sub_status_list = [];
            let serviceLineTabData = generateServiceLineUpdateData(procedureInfo);
            serviceLineTabData = {
                ...serviceLineTabData,
                responsibility_type: responsibilityType,
                claim_status:[],
                claim_sub_status: claim_sub_status_list,
                claim_pk: payload
            }

            state.responsibilityType=responsibilityType;
            /**
           * modifier validation done here
           */
            let { modifiervalidate,
                modifier1,
                modifier2,
                modifier3,
                modifier4 } = validateModifier(serviceLineTabData?.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;

            state.serviceLineDetails = {...serviceLineTabData,permittedFields:fieldServiceInfo};
            state.duplicateDataSet.serviceLineDetails = serviceLineTabData;
            state.fieldServiceInfo=fieldServiceInfo;

            /**
            * additional claim tab data processed here
            */


            let additionalTabData = generateAdditionaleUpdateData(additionalInfo, accidentList);

            additionalTabData = {
                ...additionalTabData,
               permittedFields:fieldAdditionalInfo,
                claim_pk: payload
            }

            state.additionalClaimDetails = additionalTabData;
            state.duplicateDataSet.additionalClaimDetails = additionalTabData;
            /**check for claim_alert */
            if(claimInfo.claim_alert){
                state.patientAlertModal = true;
                state.alertMessage = claimInfo.claim_alert ? claimInfo.claim_alert : "";
            }

            /**
             * setting form waring error on popup
             */
            let serverWarings = setWarningFromServer(claimInfo);
            state.formWarningData = serverWarings;
            state.editDetailsLoading = false;
        })
        //get patient additional details api rejected state
        builder.addCase(getEditDetails.rejected, (state, action) => {
            state.notifyMessage = action.payload;
            state.showNotification = true;
            state.statusTag = "error";
            state.editDetailsLoading = false;
        })

        //pos code list success case
        builder.addCase(GetServiceLocationPOSList.fulfilled, (state, actions) => {
            if (actions.payload.message == "pos_not_found") {
                state.serviceLocationPosList = [];
            }
            else {
                state.serviceLocationPosList = actions.payload;
            }
        })

        //pos code list success case
        builder.addCase(GetCPTCodeListWithDate.fulfilled, (state, actions) => {
            let { data, request } = actions.payload;
            let { serviceLineDetails, professionalClaimDetails } = current(state);
            let newData = serviceLineDetails?.procedures[request?.index];
            if (request?.value) {
                if (request?.name == "dos_from") {
                    newData = { ...newData, [request?.name]: format(request?.value, 'yyyy-MM-dd') };
                    newData = { ...newData, ["dos_to"]: format(request?.value, 'yyyy-MM-dd') };
                }
                else {
                    newData = { ...newData, [request?.name]: format(request?.value, 'yyyy-MM-dd') };
                }
            }

            if (data.length > 0) {
                newData = { ...newData, ["fee_units"]: data[0]?.units };
                newData = { ...newData, ["fee"]: decimalConverter(data[0]?.fee) };
                newData = { ...newData, ["charges"]: decimalConverter(data[0]?.fee) * parseInt(data[0]?.units) };
            }

            state.serviceLineDetails.procedures[request?.index] = newData;
            state.fee_field_status = {all:true,single:true}


            let enteredDos = format(request?.value, 'yyyy-MM-dd')
            let serviceDateFrom = getServiceDateFrom(enteredDos, serviceLineDetails?.procedures, request?.index);
            let serviceDateTo = getServiceDateTo(enteredDos, serviceLineDetails?.procedures, request?.index);
            /**
             * storing service from date and to date to 
             */

            let updateValue = {
                ...professionalClaimDetails,
                ["service_from_date"]: serviceDateFrom,
                ["service_to_date"]: serviceDateTo
            }

            state.professionalClaimDetails = updateValue;

        })

        builder.addCase(getPatientHistory.pending, (state) => {
            state.dataLoading = true;
        })

        builder.addCase(getPatientHistory.fulfilled, (state, action) => {
            state.dataLoading = false;
            if (action?.payload?.icd_codes?.length > 0) {
                state.patientHistoryICD = action?.payload?.icd_codes;
            }

            if (action?.payload?.cpt_codes?.length > 0) {
                state.patientHistoryCPT = action?.payload?.cpt_codes
            }

            state.patientHistoryModal = true;
        })

        builder.addCase(getPatientHistory.rejected, (state) => {
            state.dataLoading = false;
        })

        builder.addCase(getChargePanelsList.pending, (state) => {
            state.dataLoading = true;
        })

        builder.addCase(getChargePanelsList.fulfilled, (state, action) => {
            state.dataLoading = false;
            if (Array.isArray(action?.payload?.data)) {
                state.chargePanelsList = action?.payload?.data;
                state.chargePanelsModal = true;
            } else {
                state.notifyMessage = i18n.t('claim_beta.api_errors.error_fetching_charge_panels');
                state.showNotification = true
                state.statusTag = "error";
                state.chargePanelsModal = false;
            }
        })

        builder.addCase(getChargePanelsList.rejected, (state) => {
            state.dataLoading = false;
            state.chargePanelsList = [];
            state.notifyMessage = i18n.t('claim_beta.api_errors.error_fetching_charge_panels');
            state.showNotification = true;
            state.statusTag = "error";
            state.chargePanelsModal = false;
        })

        builder.addCase(addNewProcedureFromChargePanel.pending, (state) => {
            state.dataLoading = true;
        })

        builder.addCase(addNewProcedureFromChargePanel.fulfilled, (state, actions) => {
            // When invalid response is recieved;
            if (!actions?.payload?.data || !Array.isArray(actions?.payload?.data)) {
                state.notifyMessage =i18n.t('claim_beta.api_errors.error_fetching_panel_procedures')
                state.showNotification = true
                state.statusTag = "error";
                state.dataLoading = false;
                return;
            }

            // When valid response is recieved but the panel has not procedures;
            if (actions?.payload?.data?.length == 0) {
                state.notifyMessage = i18n.t('claim_beta.api_errors.no_available_procedures')
                state.showNotification = true
                state.statusTag = "error";
                state.dataLoading = false;
                return;
            }

            // Extract current state values using immer's current
            let { serviceLineDetails, billingInfoDetails, professionalClaimDetails, claimSubStatus} = current(state);
            const paymentDone = serviceLineDetails?.procedures?.some(procedure => procedure.payment_done);

            // Helper functions for procedure data
            const getLastProcedureRowDos = (dos_type) => {
                const lastProcedure = serviceLineDetails?.procedures?.[0];
                const dos_from = lastProcedure?.dos_from;
                const dos_to = lastProcedure?.dos_to;

                if (dos_from && dos_to) {
                    return dos_type === 'dos_from' ? dos_from : dos_type === 'dos_to' ? dos_to : "";
                }
                return "";
            }

            const getDxPointers = () => {
                let dxPointers = "";
                const pointerMapping = {
                    "1": "A", "2": "B", "3": "C", "4": "D", "5": "E", "6": "F",
                    "7": "G", "8": "H", "9": "I", "10": "J", "11": "K", "12": "L"
                };

                for (let item = 1; item <= 12; item++) {
                    const diagnosisKey = `diagnosis${item}`;
                    if (serviceLineDetails?.[diagnosisKey]) {
                        dxPointers += pointerMapping[item] || "";
                    }
                }

                return dxPointers;
            }

            const getResponsibility = () => {
                if (!billingInfoDetails?.payerInsuranceList || billingInfoDetails?.payerInsuranceList.length === 0) {
                    return RESPONSIBILITY_TYPES.find(item => item.name === "Patient")?.id || "";
                }

                const sortedList = billingInfoDetails?.payerInsuranceList?.slice().sort((a, b) => a.priority - b.priority);
                return Number(sortedList[0]?.priority) || "";
            }

            const getClaimStatusAndSubStatus = () => ({
                claim_status: paymentDone ? null : professionalClaimDetails?.claim_status,
                claim_sub_status: paymentDone ? null : professionalClaimDetails?.claim_sub_status,
            });

            // Iterate through each procedure row received from the charge panel and 
            // transform the data to match the required claim procedure row specification
            const panelProcedures = actions.payload.data || [];
            const processedProcedures = [];
            for (let i = 0; i < panelProcedures.length; i++) {
                const panelProcedure = panelProcedures[i];

                // Create new procedure object with panel data
                const newProcedure = {
                    ...newProcedureRowItem,
                    id: `New-${crypto?.randomUUID()}`,
                    // Map panel procedure properties to procedure object
                    cpt_hcpcs: panelProcedure.cpt_hcpcs || '',
                    cpt_hcpcsList: panelProcedure.cpt_name ? [{
                        id: panelProcedure.cpt_hcpcs,
                        name: panelProcedure.cpt_name,
                        label: panelProcedure.cpt_name
                    }] : [],
                    pos_id: panelProcedure.pos || '',
                    pos_id_List: panelProcedure.pos_name ? [{
                        id: panelProcedure.pos,
                        name: panelProcedure.pos_name,
                        label: panelProcedure.pos_name
                    }] : [],
                    tos_id: panelProcedure.tos || '',
                    tos_id_List: panelProcedure.tos_name ? [{
                        id: panelProcedure.tos,
                        name: panelProcedure.tos_name,
                        label: panelProcedure.tos_name
                    }] : [],
                    proced_description: panelProcedure.proced_description || '',
                    fee_units: isNaN(panelProcedure.fee_units) ? "0" : String(panelProcedure.fee_units),
                    fee: isNaN(panelProcedure.fee) ? "0" : String(panelProcedure.fee),
                    charges: isNaN(panelProcedure.charges) ? "0" : String(panelProcedure.charges),
                    line_note: panelProcedure.line_note || '',
                    drug_code: panelProcedure.drug_code || '',
                    drug_unit_type: panelProcedure.drug_unit_type || '',
                    drug_unit_value: panelProcedure.drug_unit_value || '',
                    measurement_unit_type: panelProcedure.measurement_unit_type || '',
                    measurement_unit_value: panelProcedure.measurement_unit_value || '',
                    anesthesia: panelProcedure.anesthesia || '',
                    m1_id: panelProcedure.m1_id || '',
                    m2_id: panelProcedure.m2_id || '',
                    m3_id: panelProcedure.m3_id || '',
                    m4_id: panelProcedure.m4_id || '',
                    m1List: panelProcedure.m1_name ? [{ id: panelProcedure.m1_id, name: panelProcedure.m1_name }] : [],
                    m2List: panelProcedure.m2_name ? [{ id: panelProcedure.m2_id, name: panelProcedure.m2_name }] : [],
                    m3List: panelProcedure.m3_name ? [{ id: panelProcedure.m3_id, name: panelProcedure.m3_name }] : [],
                    m4List: panelProcedure.m4_name ? [{ id: panelProcedure.m4_id, name: panelProcedure.m4_name }] : [],
                    cpt_epsdt: panelProcedure.cpt_epsdt,
                    emg: panelProcedure.emg,
                    // Add common fields for all procedures
                    dos_from: getLastProcedureRowDos('dos_from'),
                    dos_to: getLastProcedureRowDos('dos_to'),
                    dx_pointers: getDxPointers(),
                    responsibility_type: getResponsibility(),
                    ...getClaimStatusAndSubStatus()
                };

                processedProcedures.push(newProcedure)

                // Update claim sub status if applicable
                if (claimSubStatus.length > 0 && professionalClaimDetails.claim_status) {
                    let filterData = claimSubStatus?.filter(item =>
                        item.parent_claim_status_ids?.includes(professionalClaimDetails.claim_status)
                    );
                    state.serviceLineDetails.claim_sub_status[serviceLineDetails.procedures.length] = filterData;
                }
            }

            // When there's only single row currently in the service line and if it does not have a cpt slected then
            // replace procedure row completely with the panel data.
            // otherwise, spread the new panel procedure with the existing procedure rows.
            if (state.serviceLineDetails.procedures.length == 1 && !state.serviceLineDetails.procedures[0]?.cpt_hcpcs) {
                state.serviceLineDetails.procedures = processedProcedures;
            } else {
                state.serviceLineDetails.procedures = [...state.serviceLineDetails.procedures, ...processedProcedures];
            }

            // Validate modifiers after adding all procedures
            let { modifiervalidate, modifier1, modifier2, modifier3, modifier4 } =
                validateModifier(state.serviceLineDetails.procedures);

            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;

            // Reset panel-related states
            state.chargePanelsModal = false;
            state.chargePanelsList = [];

            // Automatically scroll to the last procedure row after a brief delay
            // to ensure DOM updates are complete, using smooth scrolling behavior
            setTimeout(() => {
                document.getElementById(`procedure_row_${serviceLineDetails?.procedures[serviceLineDetails?.procedures.length - 1]?.id}`)?.scrollIntoView({
                    behavior: 'smooth'
                });
            }, 200);

            // Set loading state to false as we have data
            state.dataLoading = false;
        })

        builder.addCase(addNewProcedureFromChargePanel.rejected, (state) => {
            state.dataLoading = false;
            state.notifyMessage = i18n.t('claim_beta.api_errors.error_fetching_panel_procedures')
            state.showNotification = true
            state.statusTag = "error";
        })

        builder.addCase(ActivateInactiveClaim.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(ActivateInactiveClaim.fulfilled, (state) => {
            state.dataLoading = false;
            state.notifyMessage = i18n.t('claim_beta.api_success.claim_activated_successfully')
            state.showNotification = true
            state.statusTag = "success"

            let { professionalClaimDetails } = current(state);
            let updateValue = {
                ...professionalClaimDetails,
                active: true,
                claim_inactive: false
            }
            /**
             * updating profession claim details immediately
             */
            state.professionalClaimDetails = updateValue;
        })
        builder.addCase(ActivateInactiveClaim.rejected, (state) => {
            state.dataLoading = false;
            state.notifyMessage = i18n.t('claim_beta.api_errors.error_activating_claim')
            state.showNotification = true
            state.statusTag = "error"
        }),
        /**setting identifier */
        builder.addCase(fetchIdentifiers.fulfilled, (state, actions) => {
                let { professionalClaimDetails } = current(state);
                let { data } = actions?.payload || {};
                let updateValue = {
                    ...professionalClaimDetails,
                    identifierData: data
                }

                state.professionalClaimDetails = updateValue;

        })
        builder.addCase(fetchIdentifiers.rejected, (state, actions) => {
            
            state.dataLoading = false;
            state.notifyMessage = actions.payload?.msg || i18n.t('errorMessages.commonError')
            state.showNotification = true
            state.statusTag = "error"

        })
        /**builder function to perform action on API data */
        builder.addCase(fetchUnAppliedFee.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(fetchUnAppliedFee.fulfilled, (state, actions) => {
            let { data,type } = actions.payload;
            let { serviceLineDetails} = current(state);
            let { procedures } = serviceLineDetails;

            const feeMap = data.reduce((acc, obj) => {
                const [key, value] = Object.entries(obj)[0];
                acc[key] = parseFloat(value);
                return acc;
            }, {});
            const updatedProcedures = procedures.map(proc => {
                const newFee = feeMap[proc.id];
                if (newFee !== undefined) {
                    const prevFee = parseFloat(proc.fee);
                    const units = parseFloat(proc.fee_units) || 1;

                    // If fee changed, recalculate charges
                    const charges = newFee !== prevFee ? newFee * units : proc.charges;

                    return {
                        ...proc,
                        fee: newFee.toFixed(2),
                        charges:Number(charges || 0).toFixed(2),
                    };
                }
                return proc;
            });

            state.serviceLineDetails.procedures=updatedProcedures;
            if(type=="single"){
            state.fee_field_status={
                ...state.fee_field_status,
                single:false,
                all: state.serviceLineDetails.procedures && state.serviceLineDetails.procedures.length ==1 ? false :true
            };
            }else{
                state.fee_field_status =initialState.fee_field_status
            }
            state.dataLoading = false;
        })
        builder.addCase(fetchUnAppliedFee.rejected, (state,action) => {
            state.dataLoading = false;
            state.notifyMessage = action.payload;
            state.showNotification = true;
            state.statusTag = "error";
        })
        // View Activity - Workflow
        builder.addCase(getClaimWorkflow.pending, (state) => {
            state.viewActivity.loading.workflow = true;
        })
        builder.addCase(getClaimWorkflow.fulfilled, (state, action) => {
            state.viewActivity.loading.workflow = false;
            state.viewActivity.workflowData = action.payload?.data || [];
        })
        builder.addCase(getClaimWorkflow.rejected, (state, action) => {
            state.viewActivity.loading.workflow = false;
            state.viewActivity.error = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.notifyMessage = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.showNotification = true;
            state.statusTag = "error";
        })

        // View Activity - Logs
        builder.addCase(getClaimActivityLogs.pending, (state) => {
            state.viewActivity.loading.logs = true;
        })
        builder.addCase(getClaimActivityLogs.fulfilled, (state, action) => {
            state.viewActivity.loading.logs = false;
            state.viewActivity.logsData = action.payload?.data || [];
        })
        builder.addCase(getClaimActivityLogs.rejected, (state, action) => {
            state.viewActivity.loading.logs = false;
            state.viewActivity.error = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.notifyMessage = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.showNotification = true;
            state.statusTag = "error";
        })

        builder.addCase(getEscalationComments.pending, (state) => {
            state.viewActivity.loading.comments = true;
        })
        builder.addCase(getEscalationComments.fulfilled, (state, action) => {
            state.viewActivity.loading.comments = false;
            state.viewActivity.escalationCommentsData = action.payload?.data || [];
        })
        builder.addCase(getEscalationComments.rejected, (state, action) => {
            state.viewActivity.loading.comments = false;
            state.viewActivity.error = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.notifyMessage = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.showNotification = true;
            state.statusTag = "error";
        })

        builder.addCase(getAuditorComments.pending, (state) => {
            state.viewActivity.loading.comments = true;
        })
        builder.addCase(getAuditorComments.fulfilled, (state, action) => {
            state.viewActivity.loading.comments = false;
            state.viewActivity.auditorCommentsData = action.payload?.data || [];
        })
        builder.addCase(getAuditorComments.rejected, (state, action) => {
            state.viewActivity.loading.comments = false;
            state.viewActivity.error = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.notifyMessage = action.payload?.errorMessage || i18n.t('errorMessages.commonError');
            state.showNotification = true;
            state.statusTag = "error";
        })

        // Complete Claim API
        builder.addCase(completeClaimAPI.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(completeClaimAPI.fulfilled, (state) => {
            state.dataLoading = false;
            state.showNotification = true;
            state.notifyMessage = i18n.t("claim_beta.api_success.complete_claim_success");
            state.statusTag = "success";
            state.escalationResultComplete=true
        })
        builder.addCase(completeClaimAPI.rejected, (state, action) => {
            state.dataLoading = false;
            state.showNotification = true;
            state.notifyMessage = action.payload?.message || i18n.t("claim_beta.api_errors.something_went_wrong");
            state.statusTag = "error";
            state.escalationResultComplete=false
        })
        builder.addCase(fetchPracticeUsers.fulfilled, (state, action) => {
            state.practiceUsers = action.payload;
        })
        builder.addCase(fetchSupervisorEscalationDetails.fulfilled, (state, action) => {
            state.escalationDetails = action.payload;
            state.selectedStageIds=action.payload.blocked_claim_stage_ids?.length>0 ? [action.payload.blocked_claim_stage_ids[0]] : []
            state.RequestDialogDetails={
                reasonId: action.payload.escalations[0]?.escalation_reason_id ?? "",
                explain: action.payload.escalations[0]?.description ?? "",
                role: ROLE_MANAGER,
                assignTo: action.payload.escalations[0]?.reports_to ?? "",
            }
        })
        builder.addCase(fetchSupervisorEscalationDetails.rejected, (state, action) => {
            state.showNotification = true;
            state.notifyMessage = action.payload || i18n.t("claim_beta.api_errors.something_went_wrong");
            state.statusTag = "error";
        })

        /**
         * deEscalateClaimAPI cases
         */
        builder.addCase(deEscalateClaimAPI.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(deEscalateClaimAPI.fulfilled, (state) => {
            state.dataLoading = false;
            state.showNotification = true;
            state.notifyMessage = i18n.t("claim_beta.api_success.de_escalate_success");
            state.statusTag = "success";
            state.escalationResultComplete=true;
        })
        builder.addCase(deEscalateClaimAPI.rejected, (state, action) => {
            state.dataLoading = false;
            state.showNotification = true;
            state.notifyMessage = action.payload || i18n.t("claim_beta.api_errors.something_went_wrong");
            state.statusTag = "error";
            state.escalationResultComplete=false;
        })

        /**
         * pending state of create or update a claim
         */
        builder.addCase(updateClaimAPI.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(updateClaimAPI.fulfilled, (state, actions) => {
            const { isClose = false, data = {}, claimPK } = actions.payload || {};
            const { service_lines = {} } = data;
        
            if (isClose) {
                state.isClaimClose = true;
            } else {
                // 3. Process recalculation only if data exists
                if (service_lines && service_lines?.procedures && service_lines?.procedures?.length > 0) {
                    /**
             * serviceLine tab data processed here
             */
                    let claim_sub_status_list = [];
                    let serviceLineTabData = generateServiceLineUpdateData(service_lines);
                    serviceLineTabData = {
                        ...serviceLineTabData,
                        responsibility_type: state.responsibilityType,
                        claim_status: [],
                        claim_sub_status: claim_sub_status_list,
                        claim_pk: claimPK
                    }

                    /**
                   * modifier validation done here
                   */
                    let { modifiervalidate,
                        modifier1,
                        modifier2,
                        modifier3,
                        modifier4 } = validateModifier(serviceLineTabData?.procedures);

                    state.ModifiersValidated = modifiervalidate;
                    state.inputM1Validated = modifier1;
                    state.inputM2Validated = modifier2;
                    state.inputM3Validated = modifier3;
                    state.inputM4Validated = modifier4;

                    state.serviceLineDetails = { ...serviceLineTabData, permittedFields: state.fieldServiceInfo };
                    state.duplicateDataSet.serviceLineDetails = serviceLineTabData;
                }

                // 5. Get current state snapshot safely for the duplicate backup set
                const currentState = current(state);

                state.duplicateDataSet = {
                    professionalClaimDetails: currentState.professionalClaimDetails,
                    billingInfoDetails: currentState.billingInfoDetails,
                    additionalClaimDetails: currentState.additionalClaimDetails,
                    serviceLineDetails:currentState.serviceLineDetails
                };
            }
        
            state.dataLoading = false;
        });
        
        builder.addCase(updateClaimAPI.rejected, (state, actions) => {
            // Crash prevention: Fallback if payload is undefined
            let { type = "", value = false, message = "", isopen = false, status = "error",isClose = false } = actions.payload || {};
            if (isClose) state.isClaimClose = true;
            if (type === "fee_confirmation_modal") {
                state.fee_confirmation_modal = value;
            }
        
            if (isopen) {
                state.showNotification = true;
                state.notifyMessage = message || i18n.t("claim_beta.api_errors.something_went_wrong");
                state.statusTag = status;  
            }
            state.dataLoading = false;
        })
        // claim payments Api
        builder.addCase(fetchClaimPayments.pending, (state) => {
            state.ClaimSummayLoading = true;
            state.ClaimSummayError = null;
        })
        builder
            .addCase(fetchClaimPayments.fulfilled, (state, action) => {
                state.ClaimSummayData = action.payload; 
                state.isOpen = true;
                state.ClaimSummayLoading = false;
            })
            builder.addCase(fetchClaimPayments.rejected, (state, action) => {
                state.ClaimSummayLoading = false;
                state.ClaimSummayError = action.payload;
                state.notifyMessage = action.payload|| i18n.t('errorMessages.commonError');
                state.showNotification = true;
                state.statusTag = "error";

            })
            // fetch payemntDetails Api
            builder.addCase(fetchPaymentDetail.pending, (state) => {
                state.error = null;
            });
            builder.addCase(fetchPaymentDetail.fulfilled, (state, action) => {
                const { paymentId, paymentType, details } = action.payload;
                if (state.ClaimSummayData?.payments) {
                    // Find the index of the specific payment row that was expanded
                    // Use String() casting to handle ID type mismatches (number vs string)
                    const idx = state.ClaimSummayData.payments.findIndex(p => 
                        String(p.payment_id || p.id) === String(paymentId) && 
                        String(p.payment_type || p.type) === String(paymentType)
                    );
                    // If the payment row is found in the current list
                    if (idx !== -1) {
                        state.ClaimSummayData.payments[idx] = { ...state.ClaimSummayData.payments[idx], ...details };
                    }
                }
            });
            builder.addCase(fetchPaymentDetail.rejected, (state, action) => {
                state.error = action.payload || "Failed to fetch payment details";
                state.notifyMessage = action.payload|| i18n.t('errorMessages.commonError');
                state.showNotification = true;
                state.statusTag = "error";
            });

        builder.addCase(fetchSupportReasons.pending, (state) => {
            state.dataLoading = true;
        })
        builder.addCase(fetchSupportReasons.fulfilled, (state, action) => {
            state.dataLoading = false;
            state.reasonOptions = action.payload.map((item) => ({
                id: item?.id ?? item?.pId,
                name: item?.name || item?.description || item?.full_name || item?.pName,
            })).filter((x) => x.id != null && x.name);
        })
        builder.addCase(fetchSupportReasons.rejected, (state) => {
            state.dataLoading = false;
        })
        builder.addCase(submitSupportRequest.pending, (state) => {
            state.submitting = true;
            state.dataLoading = true;
        })
        builder.addCase(submitSupportRequest.fulfilled, (state) => {
            state.submitting = false;
            state.dataLoading = false;
            state.escalationResultComplete=true
            state.showNotification = true;
            state.notifyMessage = i18n.t("claim_beta.api_success.request_support_sucess");
            state.statusTag = "success";
        })
        builder.addCase(submitSupportRequest.rejected, (state,action) => {
            state.submitting = false;
            state.dataLoading = false;
            state.escalationResultComplete=false
            state.showNotification = true;
            state.notifyMessage = action.payload|| i18n.t('errorMessages.commonError');
            state.statusTag = "error";
        });
        builder.addCase(escalateToManager.pending, (state) => {
            state.submitting = true;
            state.dataLoading = true;
        })
        builder.addCase(escalateToManager.fulfilled, (state) => {
            state.submitting = false;
            state.dataLoading = false;
            state.escalationResultComplete=true
            state.showNotification = true;
            state.notifyMessage = i18n.t("claim_beta.api_success.request_support_sucess");
            state.statusTag = "success";
        })
        builder.addCase(escalateToManager.rejected, (state,action) => {
            state.submitting = false;
            state.dataLoading = false;
            state.escalationResultComplete=false
            state.showNotification = true;
            state.notifyMessage = action.payload|| i18n.t('errorMessages.commonError');
            state.statusTag = "error";
        });

        builder.addCase(deleteProcedureItem.pending, (state) => {
            state.dataLoading = true;
        });
        
        // Handle Fulfilled
        builder.addCase(deleteProcedureItem.fulfilled, (state, action) => {
            state.dataLoading = false;
            const { procedureId, index } = action.payload;
            
            // Safely mutate the draft state directly using Redux Toolkit's built-in Immer
            if (state.serviceLineDetails?.procedures?.[index]?.id === procedureId) {
                
                // Safely initialize claim_sub_status if it doesn't exist
                if (!state.serviceLineDetails.claim_sub_status) {
                    state.serviceLineDetails.claim_sub_status = {};
                }
                state.serviceLineDetails.claim_sub_status[index] = [];
        
                // Remove the target procedure directly from the array via its index
                state.serviceLineDetails.procedures.splice(index, 1);
            }

            state.duplicateDataSet.serviceLineDetails=state.serviceLineDetails;
        
            // Validate modifiers with the newly updated array
            const { 
                modifiervalidate, 
                modifier1, 
                modifier2, 
                modifier3, 
                modifier4 
            } = validateModifier(state.serviceLineDetails.procedures);
        
            state.ModifiersValidated = modifiervalidate;
            state.inputM1Validated = modifier1;
            state.inputM2Validated = modifier2;
            state.inputM3Validated = modifier3;
            state.inputM4Validated = modifier4;
            
            // Reset modals and status states
            state.deleteProcedureModal = false;
            state.deleteProcedure = initialState.deleteProcedure;
            state.fee_field_status = initialState.fee_field_status;
        
            // Trigger success toast notification
            state.showNotification = true;
            state.notifyMessage = i18n.t('claim_beta.api_success.procedure_delete_success');
            state.statusTag = "success";
        });
        
        // Handle Rejected
        builder.addCase(deleteProcedureItem.rejected, (state, action) => {
            // FIX 1: Default to empty object to prevent runtime errors if action.payload is undefined
            const { message, payment_done, procedureId, index } = action.payload || {};
            
            state.dataLoading = false; // Turned off loading state
            
            if (payment_done) {
                state.deleteProcedureModal = true;
                state.deleteProcedure = {
                    procedureId: procedureId,
                    procedureIndex: index
                };
                return;
            }
            
            if (message) {
                state.showNotification = true;
                // FIX 2: Correctly assigned the extracted string 'message' instead of the full payload object
                state.notifyMessage = message || i18n.t('errorMessages.commonError');
                state.statusTag = "error";
            }
        });
        builder.addCase(ClaimStatusChange.pending, (state) => {
            state.loading = true
        })
        .addCase(ClaimStatusChange.fulfilled, (state,action) => {
            state.loading = false
            state.makeInProgressStatus = true;
            state.claim_id_open_in_new_tab = action.payload.id;
        })
        .addCase(ClaimStatusChange.rejected, (state, action) => {
            state.loading = false
            const { message } = action.payload;
            state.makeInProgressStatus = false;
            state.notify_message = {
                show: true,
                message: message || "",
                type: "error",
            };
        })
        builder
            .addCase(arFollowupNotes.pending, (state) => {
                state.dataLoading = true
            })
        builder
            .addCase(arFollowupNotes.rejected, (state, action) => {
                state.dataLoading = false
                state.notifyMessage = action.payload;
                state.showNotification = true;
                state.statusTag = "error"
            }),
        builder
            .addCase(ARClaimNotes.pending, (state) => {
                state.arNotes.loading = true;
            });
        builder.addCase(ARClaimNotes.fulfilled, (state,action) => {
            state.showNotification = true;
            state.statusTag = "success"
            state.arNotes.loading = false;
            state.arNotes.notes = "",
            state.arNotes.id = ""
            state.arNotes.ar_status_code = ""
            state.arNotes.ar_action_code = ""
            state.arNotes.next_followup_date = ""
            state.arNotes.selected_template_id = ""
            state.notifyMessage = action.payload?.message;
        });
        builder.addCase(ARClaimNotes.rejected, (state, actions) => {
            state.arNotes.loading = false;
            state.showNotification = true;
            state.notifyMessage = actions.payload?.message;
            state.statusTag = "error"
        });    

        builder.addCase(ListArClaimNotes.pending, (state) => {
                state.loading = true
            })
        builder.addCase(ListArClaimNotes.fulfilled, (state, action) => {
            state.loading = false
            state.arNotes.ar_notes_List = (action.payload || []).map(note => ({
                ...note,
                next_follow_up_date: YMD_TO_MDY(note.next_follow_up_date),
                last_follow_up_date: YMD_TO_MDY(note.last_follow_up_date)
            }));
                
            })
        builder
            .addCase(ListArClaimNotes.rejected, (state, action) => {
                state.loading = false
                state.notifyMessage = action.payload?.message;
                state.showNotification = true;
                state.statusTag = "error"
            })
        builder
            .addCase(onEditArClaimNotes.pending, (state) => {
                state.arNotes.loading = true
            })
        builder
            .addCase(onEditArClaimNotes.fulfilled, (state, action) => {
                const { id,ar_note_template, notes, ar_status, ar_action, next_follow_up_date, last_follow_up_date} = action.payload
                state.arNotes.loading = false
                state.arNotes = {
                    ...state.arNotes,
                    loading: false,
                    "id":id,
                    "selected_template_id": ar_note_template,
                    "notes": notes,
                    "ar_status_code": ar_status,
                    "ar_action_code": ar_action,
                    "next_followup_date": next_follow_up_date,
                    "last_follow_up_date": last_follow_up_date
                }
                //Save an identical snapshot copy to backup object
                state.original_ar_notes = {
                    notes: action.payload.notes,
                    ar_status_code: action.payload.ar_status,
                    ar_action_code: action.payload.ar_action,
                    next_followup_date: action.payload.next_follow_up_date,
                    selected_template_id: action.payload.ar_note_template
                };
            })
        builder
            .addCase(onEditArClaimNotes.rejected, (state, action) => {
                state.arNotes.loading = false
                state.notifyMessage = action.payload?.message;
                state.showNotification = true;
                state.statusTag = "error"
            })
         builder.addCase(arFollowupNotes.fulfilled, (state, action) => {
            state.dataLoading = false;
            state.arNotes.loading = false;
            state.arNotes.follow_up_notes = Array.isArray(action.payload)
                ? action.payload.map((item) => ({ 
                    ...item, 
                    name: item.template_name || item.name 
                  }))
                : [];
        });
        builder.addCase(onDeleteArClaimNotes.pending, (state) => {
            state.dataLoading = true;
        });
        builder.addCase(onDeleteArClaimNotes.fulfilled, (state) => {
            state.showNotification = true;
            state.notifyMessage = i18n.t('errorMessages.delete_success');
            state.statusTag = "success"
            state.dataLoading = false;
        });
        builder.addCase(onDeleteArClaimNotes.rejected, (state, action) => {
            state.showNotification = true;
            state.notifyMessage = action.payload?.message;
            state.statusTag = "error"
            state.dataLoading = false;
        });
        builder
        .addCase(getArClaimFilterDataSet.pending, (state) => {
            state.dropDownListData.isLoading = true;
            state.dropDownListData.isErrorGettingDropdownData = false;
        })
        .addCase(getArClaimFilterDataSet.fulfilled, (state, action) => {
            const { 
                arStatusList,
                arActionCodeList} = action.payload;
            state.dropDownListData.isLoading = false;
            state.dropDownListData.isErrorGettingDropdownData = false;
            state.dropDownListData.arStatusList = arStatusList;
            state.dropDownListData.arActionCodeList = arActionCodeList;
        })
        .addCase(getArClaimFilterDataSet.rejected, (state, action) => {
            state.dropDownListData.isLoading = false;
            state.dropDownListData.isErrorGettingDropdownData = true;
            state.showNotification = true;
            state.notifyMessage = action.payload?.errorMessage;
            state.statusTag = "error";
            state.dataLoading = false;
        })
        .addCase(getFieldList.pending, (state) => {
            state.dataLoading = true;
        })
        .addCase(getFieldList.fulfilled, (state, action) => {
            state.dataLoading = false;
            state.fieldList = action.payload
        })
        .addCase(getFieldList.rejected, (state) => {
            state.dataLoading = false;
        })
        .addCase(fetchWorkflowDetails.pending, (state) => {
            state.dataLoading = true;
            state.error = null;
        })
        .addCase(fetchWorkflowDetails.fulfilled, (state, action) => {
            state.dataLoading = false;
            const {data,method} = action.payload;
            if(method==="put" && data.length==0){
                state.openAuditReviewModal=false;
                state.masterWorkflowList=initialState.masterWorkflowList;
                state.workflowDetails=initialState.workflowDetails;
                state.auditorIssuesList=initialState.auditorIssuesList;
                state.editingIssueId=initialState.editingIssueId;
                state.auditReviewDetails=initialState.auditReviewDetails;
            }
            if (data && data.length>0) {
                state.workflowDetails = data;
                if (Array.isArray(data)) {
                    state.masterWorkflowList = data;
                }
            }
        })
        .addCase(fetchWorkflowDetails.rejected, (state, action) => {
            state.dataLoading = false;
            state.error = action.payload || i18n.t("claim_beta.api_errors.something_went_wrong");
        }).addCase(fetchAuditorIssues.pending, (state) => {
            state.loadingComments = true;
            state.auditError = null;
        })
        .addCase(fetchAuditorIssues.fulfilled, (state, action) => {
            state.loadingComments = false;
            const { rawList } = action.payload;
            state.auditorIssuesList = rawList;
        })
        .addCase(fetchAuditorIssues.rejected, (state, action) => {
            state.loadingComments = false;
            state.showNotification = true;
            state.notifyMessage = action.payload;
            state.statusTag = "error";
            state.dataLoading = false;
        })

            .addCase(submitAuditorIssue.pending, (state) => {
                state.dataLoading = true;
                state.error = null;
                state.validationError = null;
            })
            .addCase(submitAuditorIssue.fulfilled, (state, action) => {
                state.dataLoading = false;
                let { editingIssueId } = action.payload
                if (editingIssueId) {
                    state.showNotification = true;
                    state.notifyMessage = i18n.t("audit_review_popup.api_success.edit_sucess");
                    state.statusTag = "success";
                } else {
                    state.showNotification = true;
                    state.notifyMessage = i18n.t("audit_review_popup.api_success.add_success");
                    state.statusTag = "success";
                }

                state.editingIssueId = initialState.editingIssueId;
                state.auditReviewDetails = initialState.auditReviewDetails;
            })
            .addCase(submitAuditorIssue.rejected, (state, action) => {
                state.dataLoading = false;
                const errorPayload = action.payload;
                if (errorPayload?.isValidation) {
                    state.showNotification = true;
                    state.notifyMessage = errorPayload.message;
                    state.statusTag = "error";
                    state.dataLoading = false;
                }
            })
            .addCase(DeleteAuditorIssue.pending, (state) => {
                state.dataLoading = true;
            })
            .addCase(DeleteAuditorIssue.fulfilled, (state,) => {
                state.dataLoading = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t("audit_review_popup.api_success.delete_success");
                state.statusTag = "success";
            })
            .addCase(DeleteAuditorIssue.rejected, (state, action) => {
                state.showNotification = true;
                state.notifyMessage = action.payload;
                state.statusTag = "error";
            })
            .addCase(reproveStage.pending, (state) => {
                state.dataLoading = true;
                state.error = null;
                state.validationError = null;
            })
            .addCase(reproveStage.fulfilled, (state) => {
                state.dataLoading = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t("audit_review_popup.api_success.reprove_success");
                state.statusTag = "success";
            })
            .addCase(reproveStage.rejected, (state, action) => {
                state.dataLoading = false;
                const errorPayload = action.payload;
                if (errorPayload?.isValidation) {
                    state.showNotification = true;
                    state.notifyMessage = errorPayload.message;
                    state.statusTag = "error";
                    state.dataLoading = false;
                }
            })
            .addCase(approveStage.pending, (state) => {
                state.dataLoading = true;
                state.error = null;
                state.validationError = null;
            })
            .addCase(approveStage.fulfilled, (state) => {
                state.dataLoading = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t("audit_review_popup.api_success.approve_success");
                state.statusTag = "success";
            })
            .addCase(approveStage.rejected, (state, action) => {
                state.dataLoading = false;
                const errorPayload = action.payload;
                if (errorPayload?.isValidation) {
                    state.showNotification = true;
                    state.notifyMessage = errorPayload.message;
                    state.statusTag = "error";
                    state.dataLoading = false;
                }
            })
            builder
            .addCase(submitEscalationResponse.pending, (state) => {
                state.isSubmittingResponse = true;
            })
            .addCase(submitEscalationResponse.fulfilled, (state) => {
                state.isSubmittingResponse = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t('review_respond_popup.success_response')
                state.statusTag = "success";
                state.requestRespondOpen = false;
                state.isClaimClose = true;
            })
            .addCase(submitEscalationResponse.rejected, (state, action) => {
                const { type, message } = action.payload
                state.isSubmittingResponse = false;
                state.showNotification = true;
                state.notifyMessage = message
                state.statusTag = type;
            });
            // ---------------- Put Claim On Hold Handlers ----------------

        builder
            .addCase(saveClaimOnHold.pending, (state) => {
                state.dataLoading = true;
                state.holdModal.loading = true;
                state.holdModal.error = null;
            })
            .addCase(saveClaimOnHold.fulfilled, (state) => {
                state.dataLoading = false;
                state.holdModal = {
                    isOpen: false,
                    reason: "",
                    status: "",
                    options: [],
                    loading: false,
                    error: null
                };
                state.showNotification = true;
                state.notifyMessage = i18n.t('review_respond_popup.hold_save');
                state.statusTag = "success";
                state.isClaimClose = true;
            })
            .addCase(saveClaimOnHold.rejected, (state, action) => {
                state.dataLoading = false;
                state.selection={
                    claimIDs:[],
                    isAllChecked:false
                };
                const errorMessage = typeof action.payload?.message === 'string'
                    ? action.payload?.message : i18n.t('review_respond_popup.failed_submit');

                state.holdModal ={
                    error:errorMessage,
                    loading:false,
                    isOpen:false,
                    reason:"",
                    status:"",
                    options:[]
                }
                state.showNotification = true;
                state.notifyMessage = errorMessage;
                state.statusTag = "error";
            });
        builder
            .addCase(releaseClaimHold.pending, (state) => {
                state.dataLoading = true;
            })
            .addCase(releaseClaimHold.fulfilled, (state) => {
                state.dataLoading = false;
                state.releaseModal = {
                    isOpen: false,
                    reason: "",
                };
                state.selection.claimIDs = [];
                state.selection.isAllChecked = false;
                state.isClaimClose = true;      // Triggers table refresh

                // 1. Show success notification popup/banner
                state.showNotification = true;
                state.statusTag = "success";
                state.notifyMessage = i18n.t('review_respond_popup.release_save');

                // 2. Reset hold flags locally so buttons update immediately
                state.isClaimHold = false;
                if (state.professionalClaimDetails) {
                    state.professionalClaimDetails.is_claim_hold = false;
                }
            })
            .addCase(releaseClaimHold.rejected, (state, action) => {
                state.dataLoading = false;
                state.releaseModal = {
                    isOpen: false,
                    reason: "",
                };
                state.isClaimClose = false;
                state.statusTag = "error";
                state.showNotification = true;
                state.notifyMessage = action.payload?.message ?? i18n.t('review_respond_popup.failed_submit');
            });
        builder.addCase(fetchClaimHoldStatuses.fulfilled, (state, action) => {
            const { options, selectedStatus } = action.payload;
            state.holdModal.options = options;
            state.holdModal.status = selectedStatus;
        });
    },
});




export const {
    resetToInitial,
    setOpenTab,
    setLast10Sort,
    setSearchClaimsSort,
    resetSearchClaimData,
    setSearchClaimFilterData,
    onPatientDropDownSelectAction,
    updateParentComponentData,
    updateBillingInfoData,
    updatePayerInfoDetails,
    updateAdditionalInfoData,
    updateProcedureCPT,
    updatePOSList,
    updateTOSList,
    updateModifierList,
    updateProcedureOtherDetails,
    ICDTypeChange,
    HandleICDChange,
    resetNotifyWindow,
    showNotifications,
    addNewProcedureRow,
    removeProcedureRowItem,
    showModalWindow,
    setFormWarningMessages,
    setFormWaringData,
    setActiveSubTab,
    removeClaimDetails,
    getClaimDetailsByClaimPK,
    saveClaimIDWaringPopUP,
    addDiganosisVariablePatientHistory,
    removeDiganosisVariablePatientHistory,
    addCPTFrompatientHistoryTable,
    handleClaimPageUnlock,
    filterClaimSubStatus,
    clearSearchClaimResetFlag,
    changeActiveParentTab,
    openViewActivity,
    closeViewActivity,
    setActivityTab,
    addActivityComment,
    confirmVoidToggle,
    cancelVoidToggle,
    onVoidClick,
    clearData,
    closeModal,
    openModal,
    showClaimBetaNotifyWindow,
    setClaimStageIdsClicked,
    escalationDetailsChange,
    setAssignToList,
    RequestDialogChange,
    setSelectedStageIds,
    setClaimIdOpenInNewTab,
    cancelEditNotes,
    updateArNotesField,
    clearArNotes,
    setActiveTab,
    setAuditDetails,
    handleEditIssueClick,
    setHoldDetails,
    setSelectedClaimIDs,
    setCheckedAllClaim,
    setReleaseDetails
} = claimBetaSlice.actions;

export default claimBetaSlice.reducer;

